import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { resolveTabAccess } from '../auth/pageAccess';
import { friendlyApiError } from '../utils/apiError';
import { useNavigate } from 'react-router-dom';

import AdminNavTabs from '../components/AdminNavTabs';
import './AdminDashboard.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

// PriceInput component for better UX when entering prices
const PriceInput = ({ value, onChange, disabled = false, placeholder = '0.00' }) => {
  const [localValue, setLocalValue] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);

  // Initialize local value from cents prop
  React.useEffect(() => {
    if (!isFocused) {
      const cents = Number(value) || 0;
      const dollars = (cents / 100).toFixed(2);
      setLocalValue(dollars);
    }
  }, [value, isFocused]);

  const handleChange = (e) => {
    const text = e.target.value;
    // Just allow the user to type - no restrictions while typing
    setLocalValue(text);
    
    // Also try to save immediately if it's a valid number
    // This ensures the value is saved even if blur doesn't fire
    const cleaned = text.replace(/[^0-9.]/g, '');
    const dollars = parseFloat(cleaned);
    if (!isNaN(dollars) && isFinite(dollars)) {
      const cents = Math.round(dollars * 100);
      onChange(cents);
    } else if (cleaned === '' || cleaned === '0') {
      onChange(0);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    
    // Convert to cents and update parent (final cleanup)
    const cleaned = localValue.replace(/[^0-9.]/g, '');
    const dollars = parseFloat(cleaned || '0');
    
    if (isNaN(dollars) || !isFinite(dollars)) {
      onChange(0);
      return;
    }
    
    const cents = Math.round(dollars * 100);
    onChange(cents);
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    // Select all text on focus for easy editing
    e.target.select();
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
};

const centsToDollars = (cents) => {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  return (n / 100).toFixed(2);
};

const dollarsToCents = (dollars) => {
  const n = Number(dollars);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
};

const fallbackDefaults = {
  storeMode: '',
  posProvider: 'SQUARE',
  reports: {
    enabledReportTypes: ['orders', 'labels', 'users', 'fulfillment'],
  },
  posPayments: {
    online_enabled: true,
    in_store_enabled: true,
  },
  posPrinting: {
    autoPrintNewOrders: false,
    orderDetailsOnly: false,
  },
  posSla: {
    enabled: false,
    greenMinutes: 5,
    orangeMinutes: 10,
    redMinutes: 15,
  },
  orderMessages: {
    pickupMessage: '',
    deliveryMessage: '',
  },
  posTips: {
    enabled: false,
    presetPercentages: [15, 18, 20, 25],
    allowCustom: true,
    allowNoTip: true,
    calculateOn: 'subtotal',
    defaultPercentage: null,
    displayMode: 'inline_collapsed',
  },
  posDeliveryLinks: {
    enabled: false,
    vendors: [],
  },
  delivery: {
    enabled: false,
    fee_cents: 0,
    minimum_order_cents: 0,
    radius_restriction: {
      enabled: false,
      base_zip_code: '',
      max_radius_miles: 10.0,
    },
  },
  addressAutocomplete: {
    provider: 'geoapify',
    enabled: false,
    apiKey: '',
  },
  otter: {
    enabled: false,
    api_key: '',
    api_secret: '',
    store_id: '',
    webhook_url: '',
    menu_sync_enabled: false,
    menu_sync_mode: 'manual',
    last_menu_sync_timestamp: null,
  },
  // Delivery Integrations enablement section (additive). Groups one toggle per
  // delivery provider (Uber Eats, Otter, ...). Mirrors the backend
  // `delivery_integrations` shape. Every toggle defaults to OFF.
  delivery_integrations: {
    ubereats: {
      enabled: false,
      environment: 'sandbox',
      client_id: '',
      // Write-only: the stored secret is never returned by the API. The GET
      // response sends a mask sentinel + `has_secret` flag instead.
      client_secret: '',
      has_secret: false,
      store_id: '',
      last_menu_sync_timestamp: null,
    },
    doordash: {
      enabled: false,
      environment: 'sandbox',
      developer_id: '',
      key_id: '',
      // Write-only: the stored signing secret is never returned by the API. The
      // GET response sends a mask sentinel + `has_secret` flag instead.
      signing_secret: '',
      has_secret: false,
      store_id: '',
      last_menu_sync_timestamp: null,
    },
    otter: {
      enabled: false,
    },
  },
  orderingClosedHours: {
    enabled: false,
    timezone: 'America/New_York',
    message: '',
    oneOff: [],
    weekly: [],
  },
  serviceWindows: {
    enabled: false,
    timezone: 'America/New_York',
    windows: [],
  },
  shipping: {
    standard: { enabled: true, label: 'Standard Shipping', amount_cents: 499, min_days: 3, max_days: 5, currency: 'usd' },
    express: { enabled: false, label: 'Express Shipping', amount_cents: 999, min_days: 1, max_days: 2, currency: 'usd' },
    free: { enabled: true, label: 'Free Shipping', threshold_cents: 2000, amount_cents: 0, min_days: 5, max_days: 8, currency: 'usd' },
    allowed_countries: ['US'],
  },
  tax: { stripe_automatic_tax_enabled: false },
  features: {
    mock_online_payment_enabled: false,
    pos_discount_codes_enabled: false,
    chatbot_enabled: false,
  },
  optional_sections: {
    crochet_features: {
      enabled: false,
      title: 'Menu features',
      html:
        '<div class="crochet-features">' +
        '<ul class="feature-bullets">' +
        '<li><strong>Freshly prepared:</strong> made to order with quality ingredients.</li>' +
        '<li><strong>Fast ordering:</strong> browse the menu, search items, and order in seconds.</li>' +
        '<li><strong>Pickup-friendly:</strong> ideal for takeout and busy schedules.</li>' +
        '<li><strong>Seasonal specials:</strong> rotating favorites and limited-time dishes.</li>' +
        '</ul>' +
        '<p><a class="feature-cta" href="/pos">View the menu</a></p>' +
        '</div>',
    },
    events: { enabled: false, title: 'Events', html: '' },
    reviews: { enabled: false, title: 'Reviews', auto_approve: false, double_opt_in: true },
  },
};

// Otter Analytics Section Component
const OtterAnalyticsSection = ({ tenantId }) => {
  const [analytics, setAnalytics] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [dateRange, setDateRange] = React.useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    end_date: new Date().toISOString().split('T')[0] // today
  });

  const fetchAnalytics = async () => {
    if (!tenantId) return;
    
    setLoading(true);
    setError('');
    
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      
      const params = new URLSearchParams({
        tenant_id: tenantId,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
      });
      
      const response = await fetch(`${API_BASE_URL}/admin/otter/analytics?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch analytics: ${response.statusText}`);
      }
      
      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      console.error('Error fetching Otter analytics:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = async () => {
    if (!tenantId) return;
    
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      
      const params = new URLSearchParams({
        tenant_id: tenantId,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        export: 'csv'
      });
      
      const response = await fetch(`${API_BASE_URL}/admin/otter/analytics?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to export analytics');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `otter_analytics_${dateRange.start_date}_to_${dateRange.end_date}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error exporting analytics:', err);
      setError(err.message || 'Failed to export analytics');
    }
  };

  React.useEffect(() => {
    fetchAnalytics();
  }, [tenantId]);

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <div style={{ marginTop: 24, padding: 16, background: 'rgba(249, 250, 251, 1)', borderRadius: 12, border: '1px solid rgba(148, 163, 184, 0.25)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#4c1d95' }}>📊 Otter Analytics</h3>
        <button
          type="button"
          onClick={exportCSV}
          disabled={loading || !analytics}
          style={{
            padding: '6px 12px',
            background: '#4c1d95',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: loading || !analytics ? 'not-allowed' : 'pointer',
            opacity: loading || !analytics ? 0.5 : 1,
            fontSize: '0.9rem'
          }}
        >
          Export CSV
        </button>
      </div>

      {/* Date Range Selector */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: '0.9rem', fontWeight: 600 }}>Start Date</label>
          <input
            type="date"
            value={dateRange.start_date}
            onChange={(e) => setDateRange(prev => ({ ...prev, start_date: e.target.value }))}
            style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid rgba(148, 163, 184, 0.35)' }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: '0.9rem', fontWeight: 600 }}>End Date</label>
          <input
            type="date"
            value={dateRange.end_date}
            onChange={(e) => setDateRange(prev => ({ ...prev, end_date: e.target.value }))}
            style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid rgba(148, 163, 184, 0.35)' }}
          />
        </div>
        <button
          type="button"
          onClick={fetchAnalytics}
          disabled={loading}
          style={{
            marginTop: 24,
            padding: '8px 16px',
            background: '#059669',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1
          }}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div style={{ padding: 12, background: 'rgba(239, 68, 68, 0.08)', borderRadius: 8, border: '1px solid rgba(239, 68, 68, 0.25)', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, color: '#dc2626' }}>Error: {error}</div>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 24, color: 'rgba(15, 23, 42, 0.6)' }}>
          Loading analytics...
        </div>
      )}

      {!loading && analytics && (
        <>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div style={{ padding: 16, background: '#fff', borderRadius: 8, border: '1px solid rgba(148, 163, 184, 0.25)' }}>
              <div style={{ fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.6)', marginBottom: 4 }}>Total Orders</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#4c1d95' }}>{analytics.total_orders}</div>
            </div>
            <div style={{ padding: 16, background: '#fff', borderRadius: 8, border: '1px solid rgba(148, 163, 184, 0.25)' }}>
              <div style={{ fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.6)', marginBottom: 4 }}>Total Revenue</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#059669' }}>{formatCurrency(analytics.total_revenue)}</div>
            </div>
            <div style={{ padding: 16, background: '#fff', borderRadius: 8, border: '1px solid rgba(148, 163, 184, 0.25)' }}>
              <div style={{ fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.6)', marginBottom: 4 }}>Avg Order Value</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#0284c7' }}>{formatCurrency(analytics.average_order_value)}</div>
            </div>
          </div>

          {/* Orders by Channel */}
          {Object.keys(analytics.orders_by_channel || {}).length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 12, color: '#1e293b' }}>Orders by Delivery Channel</h4>
              <div style={{ background: '#fff', borderRadius: 8, border: '1px solid rgba(148, 163, 184, 0.25)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'rgba(249, 250, 251, 1)', borderBottom: '1px solid rgba(148, 163, 184, 0.25)' }}>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: '0.9rem', fontWeight: 600 }}>Channel</th>
                      <th style={{ padding: 12, textAlign: 'right', fontSize: '0.9rem', fontWeight: 600 }}>Orders</th>
                      <th style={{ padding: 12, textAlign: 'right', fontSize: '0.9rem', fontWeight: 600 }}>Revenue</th>
                      <th style={{ padding: 12, textAlign: 'right', fontSize: '0.9rem', fontWeight: 600 }}>Avg Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(analytics.orders_by_channel).map(([channel, data]) => (
                      <tr key={channel} style={{ borderBottom: '1px solid rgba(148, 163, 184, 0.15)' }}>
                        <td style={{ padding: 12, fontSize: '0.9rem' }}>{channel}</td>
                        <td style={{ padding: 12, textAlign: 'right', fontSize: '0.9rem' }}>{data.count}</td>
                        <td style={{ padding: 12, textAlign: 'right', fontSize: '0.9rem' }}>{formatCurrency(data.revenue)}</td>
                        <td style={{ padding: 12, textAlign: 'right', fontSize: '0.9rem' }}>{formatCurrency(data.average_order_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Orders by Status */}
          {Object.keys(analytics.orders_by_status || {}).length > 0 && (
            <div>
              <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 12, color: '#1e293b' }}>Orders by Status</h4>
              <div style={{ background: '#fff', borderRadius: 8, border: '1px solid rgba(148, 163, 184, 0.25)', padding: 12 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {Object.entries(analytics.orders_by_status).map(([status, count]) => (
                    <div key={status} style={{ padding: '6px 12px', background: 'rgba(249, 250, 251, 1)', borderRadius: 6, border: '1px solid rgba(148, 163, 184, 0.25)' }}>
                      <span style={{ fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.6)' }}>{status}: </span>
                      <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default function GeneralSettings() {
  const navigate = useNavigate();

  const statusRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [username, setUsername] = useState('');

  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const [source, setSource] = useState('legacy');

  const [form, setForm] = useState(() => fallbackDefaults);

  // Write-only Uber Eats client secret. We NEVER prefill the stored secret; the
  // admin only types here to set/replace it. An empty string on save means
  // "leave the stored secret unchanged".
  const [ubereatsSecretInput, setUbereatsSecretInput] = useState('');

  // Inline Uber Eats Test Connection result rendered next to the card buttons
  // (in addition to the page-level banner) so the outcome is visible without
  // scrolling. Shape: { ok: boolean, message: string } | null.
  const [ueTestResult, setUeTestResult] = useState(null);

  // Optional selective Uber Eats menu sync: a comma-separated list of Product
  // IDs. When empty, "Sync Menu Now" syncs the full catalog (existing default).
  const [ueSyncProductIds, setUeSyncProductIds] = useState('');

  // Write-only DoorDash signing secret. We NEVER prefill the stored secret; the
  // admin only types here to set/replace it. An empty string on save means
  // "leave the stored secret unchanged".
  const [doordashSecretInput, setDoordashSecretInput] = useState('');

  const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const makeId = () => {
    try {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID().replace(/-/g, '');
      }
    } catch {
      // ignore
    }
    return `${Date.now()}${Math.floor(Math.random() * 1e9)}`;
  };

  const updateOrderingClosedHours = (patch) => {
    setForm((prev) => ({
      ...prev,
      orderingClosedHours: {
        ...(fallbackDefaults.orderingClosedHours || {}),
        ...((prev.orderingClosedHours && typeof prev.orderingClosedHours === 'object') ? prev.orderingClosedHours : {}),
        ...(patch || {}),
      },
    }));
  };

  const updateServiceWindows = (patch) => {
    setForm((prev) => ({
      ...prev,
      serviceWindows: {
        ...(fallbackDefaults.serviceWindows || {}),
        ...((prev.serviceWindows && typeof prev.serviceWindows === 'object') ? prev.serviceWindows : {}),
        ...(patch || {}),
      },
    }));
  };

  const addServiceWindowRule = () => {
    const id = makeId();
    setForm((prev) => {
      const base = (prev.serviceWindows && typeof prev.serviceWindows === 'object') ? prev.serviceWindows : {};
      const list = Array.isArray(base.windows) ? base.windows : [];
      const next = [...list, { id, enabled: true, name: '', start_hhmm: '09:00', end_hhmm: '17:00', days: ['ALL'] }];
      return {
        ...prev,
        serviceWindows: {
          ...(fallbackDefaults.serviceWindows || {}),
          ...base,
          windows: next,
        },
      };
    });
  };

  const updateServiceWindowRule = (id, patch) => {
    setForm((prev) => {
      const base = (prev.serviceWindows && typeof prev.serviceWindows === 'object') ? prev.serviceWindows : {};
      const list = Array.isArray(base.windows) ? base.windows : [];
      return {
        ...prev,
        serviceWindows: {
          ...(fallbackDefaults.serviceWindows || {}),
          ...base,
          windows: list.map((it) => (String(it?.id || '') === String(id) ? { ...it, ...patch } : it)),
        },
      };
    });
  };

  const deleteServiceWindowRule = (id) => {
    setForm((prev) => {
      const base = (prev.serviceWindows && typeof prev.serviceWindows === 'object') ? prev.serviceWindows : {};
      const list = Array.isArray(base.windows) ? base.windows : [];
      return {
        ...prev,
        serviceWindows: {
          ...(fallbackDefaults.serviceWindows || {}),
          ...base,
          windows: list.filter((it) => String(it?.id || '') !== String(id)),
        },
      };
    });
  };

  const updatePosDeliveryLinks = (patch) => {
    setForm((prev) => ({
      ...prev,
      posDeliveryLinks: {
        ...(fallbackDefaults.posDeliveryLinks || {}),
        ...((prev.posDeliveryLinks && typeof prev.posDeliveryLinks === 'object') ? prev.posDeliveryLinks : {}),
        ...(patch || {}),
      },
    }));
  };

  const addDeliveryVendor = () => {
    const id = makeId();
    setForm((prev) => {
      const base = (prev.posDeliveryLinks && typeof prev.posDeliveryLinks === 'object') ? prev.posDeliveryLinks : {};
      const vendors = Array.isArray(base.vendors) ? base.vendors : [];
      const next = [...vendors, { id, name: '', url: '', imageUrl: '' }];
      return {
        ...prev,
        posDeliveryLinks: {
          ...(fallbackDefaults.posDeliveryLinks || {}),
          ...base,
          vendors: next,
        },
      };
    });
  };

  const updateDeliveryVendor = (id, patch) => {
    setForm((prev) => {
      const base = (prev.posDeliveryLinks && typeof prev.posDeliveryLinks === 'object') ? prev.posDeliveryLinks : {};
      const vendors = Array.isArray(base.vendors) ? base.vendors : [];
      return {
        ...prev,
        posDeliveryLinks: {
          ...(fallbackDefaults.posDeliveryLinks || {}),
          ...base,
          vendors: vendors.map((it) => (String(it?.id || '') === String(id) ? { ...it, ...patch } : it)),
        },
      };
    });
  };

  const deleteDeliveryVendor = (id) => {
    setForm((prev) => {
      const base = (prev.posDeliveryLinks && typeof prev.posDeliveryLinks === 'object') ? prev.posDeliveryLinks : {};
      const vendors = Array.isArray(base.vendors) ? base.vendors : [];
      return {
        ...prev,
        posDeliveryLinks: {
          ...(fallbackDefaults.posDeliveryLinks || {}),
          ...base,
          vendors: vendors.filter((it) => String(it?.id || '') !== String(id)),
        },
      };
    });
  };

  const handleDeliveryVendorImageUpload = async (vendorId, file) => {
    setError('');
    setStatusMessage('');

    if (!file) {
      updateDeliveryVendor(vendorId, { imageUpload: undefined });
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      const maxMb = (MAX_IMAGE_BYTES / (1024 * 1024)).toFixed(1);
      setError(`Image is too large. Please choose a file under ${maxMb} MB.`);
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError('Unsupported file type. Please upload PNG, JPG, WEBP, or SVG images.');
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      const uploadPayload = {
        data: dataUrl,
        filename: file.name,
        mimeType: file.type,
      };
      updateDeliveryVendor(vendorId, { imageUpload: uploadPayload });
    } catch (fileError) {
      console.error('Image read error:', fileError);
      setError('Failed to read selected image. Please try again.');
    }
  };

  const addWeeklyClosedRule = () => {
    const id = makeId();
    setForm((prev) => {
      const base = (prev.orderingClosedHours && typeof prev.orderingClosedHours === 'object') ? prev.orderingClosedHours : {};
      const weekly = Array.isArray(base.weekly) ? base.weekly : [];
      const next = [...weekly, { id, enabled: true, weekday: 'MON', start_hhmm: '09:00', end_hhmm: '17:00', label: '' }];
      return {
        ...prev,
        orderingClosedHours: {
          ...(fallbackDefaults.orderingClosedHours || {}),
          ...base,
          weekly: next,
        },
      };
    });
  };

  const updateWeeklyClosedRule = (id, patch) => {
    setForm((prev) => {
      const base = (prev.orderingClosedHours && typeof prev.orderingClosedHours === 'object') ? prev.orderingClosedHours : {};
      const weekly = Array.isArray(base.weekly) ? base.weekly : [];
      return {
        ...prev,
        orderingClosedHours: {
          ...(fallbackDefaults.orderingClosedHours || {}),
          ...base,
          weekly: weekly.map((it) => (String(it?.id || '') === String(id) ? { ...it, ...patch } : it)),
        },
      };
    });
  };

  const deleteWeeklyClosedRule = (id) => {
    setForm((prev) => {
      const base = (prev.orderingClosedHours && typeof prev.orderingClosedHours === 'object') ? prev.orderingClosedHours : {};
      const weekly = Array.isArray(base.weekly) ? base.weekly : [];
      return {
        ...prev,
        orderingClosedHours: {
          ...(fallbackDefaults.orderingClosedHours || {}),
          ...base,
          weekly: weekly.filter((it) => String(it?.id || '') !== String(id)),
        },
      };
    });
  };

  const addOneOffClosedRule = () => {
    const id = makeId();
    const now = new Date();
    const start = new Date(now.getTime() + 60 * 60 * 1000);
    const end = new Date(now.getTime() + 2 * 60 * 60 * 1000);
    const toLocalInput = (dt) => {
      const pad = (n) => String(n).padStart(2, '0');
      return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
    };

    setForm((prev) => {
      const base = (prev.orderingClosedHours && typeof prev.orderingClosedHours === 'object') ? prev.orderingClosedHours : {};
      const oneOff = Array.isArray(base.oneOff) ? base.oneOff : [];
      const next = [...oneOff, { id, enabled: true, start_iso: toLocalInput(start), end_iso: toLocalInput(end), label: '' }];
      return {
        ...prev,
        orderingClosedHours: {
          ...(fallbackDefaults.orderingClosedHours || {}),
          ...base,
          oneOff: next,
        },
      };
    });
  };

  const updateOneOffClosedRule = (id, patch) => {
    setForm((prev) => {
      const base = (prev.orderingClosedHours && typeof prev.orderingClosedHours === 'object') ? prev.orderingClosedHours : {};
      const oneOff = Array.isArray(base.oneOff) ? base.oneOff : [];
      return {
        ...prev,
        orderingClosedHours: {
          ...(fallbackDefaults.orderingClosedHours || {}),
          ...base,
          oneOff: oneOff.map((it) => (String(it?.id || '') === String(id) ? { ...it, ...patch } : it)),
        },
      };
    });
  };

  const deleteOneOffClosedRule = (id) => {
    setForm((prev) => {
      const base = (prev.orderingClosedHours && typeof prev.orderingClosedHours === 'object') ? prev.orderingClosedHours : {};
      const oneOff = Array.isArray(base.oneOff) ? base.oneOff : [];
      return {
        ...prev,
        orderingClosedHours: {
          ...(fallbackDefaults.orderingClosedHours || {}),
          ...base,
          oneOff: oneOff.filter((it) => String(it?.id || '') !== String(id)),
        },
      };
    });
  };

  const legacyBanner = useMemo(() => source === 'legacy', [source]);

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const access = await resolveTabAccess(session, 'General Settings');
        if (!access.allowed) {
          navigate(access.isPosAdmin ? '/admin/channels/pos/queue' : '/admin/login');
          return;
        }

        setUsername(user.username || user.attributes?.email || 'Admin');
        await fetchSettings();
      } catch (e) {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchSettings = async () => {
    if (!API_BASE_URL) return;
    try {
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/general`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load general settings');

      const incoming = data?.settings && typeof data.settings === 'object' ? data.settings : null;
      if (incoming) {
        setForm({ ...fallbackDefaults, ...incoming });
      } else {
        setForm(fallbackDefaults);
      }
      // Reset the write-only secret input on every (re)load — the stored secret
      // is never echoed back, so the field always starts empty.
      setUbereatsSecretInput('');
      setDoordashSecretInput('');
      setSource(data?.source || 'legacy');
    } catch (e) {
      setError(e?.message || 'Failed to load settings');
      setForm(fallbackDefaults);
      setSource('legacy');
    }
  };

  const updateShipping = (segment, patch) => {
    setForm((prev) => {
      const shipping = { ...(prev.shipping || {}) };
      const seg = { ...(shipping[segment] || {}) };
      shipping[segment] = { ...seg, ...patch };
      return { ...prev, shipping };
    });
  };

  const updateTax = (patch) => {
    setForm((prev) => ({ ...prev, tax: { ...(prev.tax || {}), ...patch } }));
  };

  const updateFeatures = (patch) => {
    setForm((prev) => ({ ...prev, features: { ...(prev.features || {}), ...patch } }));
  };

  const updateReports = (patch) => {
    setForm((prev) => ({
      ...prev,
      reports: {
        ...((fallbackDefaults.reports && typeof fallbackDefaults.reports === 'object') ? fallbackDefaults.reports : {}),
        ...((prev.reports && typeof prev.reports === 'object') ? prev.reports : {}),
        ...(patch || {}),
      },
    }));
  };

  const updatePosPayments = (patch) => {
    setForm((prev) => ({
      ...prev,
      posPayments: {
        ...(fallbackDefaults.posPayments || {}),
        ...((prev.posPayments && typeof prev.posPayments === 'object') ? prev.posPayments : {}),
        ...(patch || {}),
      },
    }));
  };

  const updatePosPrinting = (patch) => {
    setForm((prev) => ({
      ...prev,
      posPrinting: {
        ...(fallbackDefaults.posPrinting || {}),
        ...((prev.posPrinting && typeof prev.posPrinting === 'object') ? prev.posPrinting : {}),
        ...(patch || {}),
      },
    }));
  };

  const updatePosSla = (patch) => {
    setForm((prev) => ({
      ...prev,
      posSla: {
        ...(fallbackDefaults.posSla || {}),
        ...((prev.posSla && typeof prev.posSla === 'object') ? prev.posSla : {}),
        ...(patch || {}),
      },
    }));
  };

  const updatePosTips = (patch) => {
    setForm((prev) => ({
      ...prev,
      posTips: {
        ...(fallbackDefaults.posTips || {}),
        ...((prev.posTips && typeof prev.posTips === 'object') ? prev.posTips : {}),
        ...(patch || {}),
      },
    }));
  };

  const updateOptionalCrochetFeatures = (patch) => {
    setForm((prev) => {
      const optional_sections = { ...(prev.optional_sections || {}) };
      const crochet_features = { ...(optional_sections.crochet_features || fallbackDefaults.optional_sections.crochet_features) };
      optional_sections.crochet_features = { ...crochet_features, ...patch };
      return { ...prev, optional_sections };
    });
  };

  const updateOptionalEvents = (patch) => {
    setForm((prev) => {
      const optional_sections = { ...(prev.optional_sections || {}) };
      const events = { ...(optional_sections.events || fallbackDefaults.optional_sections.events) };
      optional_sections.events = { ...events, ...patch };
      return { ...prev, optional_sections };
    });
  };

  const updateOptionalReviews = (patch) => {
    setForm((prev) => {
      const optional_sections = { ...(prev.optional_sections || {}) };
      const reviews = { ...(optional_sections.reviews || fallbackDefaults.optional_sections.reviews) };
      optional_sections.reviews = { ...reviews, ...patch };
      return { ...prev, optional_sections };
    });
  };

  const save = async () => {
    if (!API_BASE_URL) return;
    try {
      setSaving(true);
      setError('');
      setStatusMessage('');

      const enabledReportTypesRaw = Array.isArray(form?.reports?.enabledReportTypes) ? form.reports.enabledReportTypes : [];
      const allowedReportTypes = new Set(['orders', 'labels', 'users', 'fulfillment', 'online_sales']);
      const enabledReportTypes = [];
      const seen = new Set();
      for (const t of enabledReportTypesRaw) {
        const v = String(t || '').trim().toLowerCase();
        if (allowedReportTypes.has(v) && !seen.has(v)) {
          enabledReportTypes.push(v);
          seen.add(v);
        }
      }
      if (!enabledReportTypes.length) {
        throw new Error('At least one report type must be enabled');
      }

      const posOnlineEnabled = Boolean(form?.posPayments?.online_enabled ?? true);
      const posInStoreEnabled = Boolean(form?.posPayments?.in_store_enabled ?? true);
      if (!posOnlineEnabled && !posInStoreEnabled) {
        throw new Error('Cannot disable both online and in-store POS payments');
      }

      const posSlaEnabled = Boolean(form?.posSla?.enabled);
      const posSlaGreen = Number(form?.posSla?.greenMinutes);
      const posSlaOrange = Number(form?.posSla?.orangeMinutes);
      const posSlaRed = Number(form?.posSla?.redMinutes);
      if (posSlaEnabled) {
        if (
          !Number.isFinite(posSlaGreen) ||
          !Number.isFinite(posSlaOrange) ||
          !Number.isFinite(posSlaRed) ||
          posSlaGreen <= 0 ||
          posSlaOrange <= 0 ||
          posSlaRed <= 0
        ) {
          throw new Error('SLA green, orange, and red minutes are all required (and must be positive) when SLA indicators are enabled');
        }
        if (!(posSlaGreen < posSlaOrange && posSlaOrange < posSlaRed)) {
          throw new Error('SLA thresholds must satisfy green < orange < red minutes');
        }
      }

      const posDeliveryEnabled = Boolean(form?.posDeliveryLinks?.enabled);
      const posDeliveryVendors = Array.isArray(form?.posDeliveryLinks?.vendors) ? form.posDeliveryLinks.vendors : [];
      if (posDeliveryEnabled) {
        for (const vendor of posDeliveryVendors) {
          const name = String(vendor?.name || '').trim();
          const url = String(vendor?.url || '').trim();
          if (!name) throw new Error('Delivery provider name is required');
          if (!url) throw new Error('Delivery provider URL is required');
          if (!/^https?:\/\//i.test(url)) throw new Error('Delivery provider URL must start with http:// or https://');
        }
      }

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const payload = {
        settings: {
          storeMode: String(form?.storeMode || ''),
          posProvider: String(form?.posProvider || 'SQUARE'),
          reports: {
            enabledReportTypes,
          },
          posPayments: {
            online_enabled: Boolean(form?.posPayments?.online_enabled ?? true),
            in_store_enabled: Boolean(form?.posPayments?.in_store_enabled ?? true),
          },
          posPrinting: {
            autoPrintNewOrders: Boolean(form?.posPrinting?.autoPrintNewOrders),
            orderDetailsOnly: Boolean(form?.posPrinting?.orderDetailsOnly),
          },
          posSla: {
            enabled: posSlaEnabled,
            greenMinutes: Number.isFinite(posSlaGreen) ? posSlaGreen : 5,
            orangeMinutes: Number.isFinite(posSlaOrange) ? posSlaOrange : 10,
            redMinutes: Number.isFinite(posSlaRed) ? posSlaRed : 15,
          },
          orderMessages: {
            pickupMessage: String(form?.orderMessages?.pickupMessage || ''),
            deliveryMessage: String(form?.orderMessages?.deliveryMessage || ''),
          },
          posTips: {
            enabled: Boolean(form?.posTips?.enabled),
            presetPercentages: Array.isArray(form?.posTips?.presetPercentages) 
              ? form.posTips.presetPercentages.filter(p => Number.isFinite(Number(p)) && Number(p) > 0)
              : [15, 18, 20, 25],
            allowCustom: Boolean(form?.posTips?.allowCustom ?? true),
            allowNoTip: Boolean(form?.posTips?.allowNoTip ?? true),
            calculateOn: String(form?.posTips?.calculateOn || 'subtotal'),
            defaultPercentage: Number.isFinite(Number(form?.posTips?.defaultPercentage)) ? Number(form.posTips.defaultPercentage) : null,
            displayMode: String(form?.posTips?.displayMode || 'inline_collapsed'),
          },
          posDeliveryLinks: {
            enabled: posDeliveryEnabled,
            vendors: posDeliveryVendors.map((vendor) => ({
              id: vendor?.id,
              name: vendor?.name,
              url: vendor?.url,
              imageUrl: vendor?.imageUrl,
              imageUpload: vendor?.imageUpload,
            })),
          },
          delivery: {
            enabled: Boolean(form?.delivery?.enabled),
            fee_cents: Number.isFinite(Number(form?.delivery?.fee_cents)) ? Number(form.delivery.fee_cents) : 0,
            minimum_order_cents: Number.isFinite(Number(form?.delivery?.minimum_order_cents)) ? Number(form.delivery.minimum_order_cents) : 0,
            radius_restriction: {
              enabled: Boolean(form?.delivery?.radius_restriction?.enabled),
              base_zip_code: String(form?.delivery?.radius_restriction?.base_zip_code || '').trim(),
              max_radius_miles: Number.isFinite(Number(form?.delivery?.radius_restriction?.max_radius_miles)) ? Number(form.delivery.radius_restriction.max_radius_miles) : 10.0,
            },
          },
          addressAutocomplete: {
            provider: String(form?.addressAutocomplete?.provider || 'geoapify'),
            enabled: Boolean(form?.addressAutocomplete?.enabled),
          },
          delivery_integrations: (() => {
            const di = (form?.delivery_integrations && typeof form.delivery_integrations === 'object')
              ? form.delivery_integrations
              : {};
            const ue = (di.ubereats && typeof di.ubereats === 'object') ? di.ubereats : {};
            const dd = (di.doordash && typeof di.doordash === 'object') ? di.doordash : {};
            const ot = (di.otter && typeof di.otter === 'object') ? di.otter : {};

            const ubereats = {
              enabled: Boolean(ue.enabled),
              environment: ue.environment === 'production' ? 'production' : 'sandbox',
              client_id: String(ue.client_id || '').trim(),
              store_id: String(ue.store_id || '').trim(),
              last_menu_sync_timestamp: ue.last_menu_sync_timestamp ?? null,
            };
            // Write-only secret: only send a value when the admin typed a new
            // one. Omitting the field tells the backend to preserve the stored
            // secret (it never round-trips through the UI).
            const typedSecret = String(ubereatsSecretInput || '').trim();
            if (typedSecret) {
              ubereats.client_secret = typedSecret;
            }

            const doordash = {
              enabled: Boolean(dd.enabled),
              environment: dd.environment === 'production' ? 'production' : 'sandbox',
              developer_id: String(dd.developer_id || '').trim(),
              key_id: String(dd.key_id || '').trim(),
              store_id: String(dd.store_id || '').trim(),
              last_menu_sync_timestamp: dd.last_menu_sync_timestamp ?? null,
            };
            // Write-only signing secret: only send a value when the admin typed
            // a new one. Omitting the field tells the backend to preserve the
            // stored secret (it never round-trips through the UI).
            const typedDoordashSecret = String(doordashSecretInput || '').trim();
            if (typedDoordashSecret) {
              doordash.signing_secret = typedDoordashSecret;
            }

            return {
              ubereats,
              doordash,
              otter: {
                // The relocated Otter toggle is authoritative; the backend
                // propagates it to the top-level Otter block. `form.otter.enabled`
                // is the source of truth shared with the detailed Otter section.
                enabled: Boolean(form?.otter?.enabled ?? ot.enabled),
              },
            };
          })(),
          orderingClosedHours: {
            enabled: Boolean(form?.orderingClosedHours?.enabled),
            timezone: String(form?.orderingClosedHours?.timezone || 'America/New_York'),
            message: String(form?.orderingClosedHours?.message || ''),
            oneOff: Array.isArray(form?.orderingClosedHours?.oneOff) ? form.orderingClosedHours.oneOff : [],
            weekly: Array.isArray(form?.orderingClosedHours?.weekly) ? form.orderingClosedHours.weekly : [],
          },
          serviceWindows: {
            enabled: Boolean(form?.serviceWindows?.enabled),
            timezone: String(form?.serviceWindows?.timezone || 'America/New_York'),
            windows: Array.isArray(form?.serviceWindows?.windows) ? form.serviceWindows.windows : [],
          },
          features: {
            mock_online_payment_enabled: Boolean(form?.features?.mock_online_payment_enabled),
            pos_discount_codes_enabled: Boolean(form?.features?.pos_discount_codes_enabled),
            chatbot_enabled: Boolean(form?.features?.chatbot_enabled),
          },
          shipping: {
            allowed_countries: form?.shipping?.allowed_countries || ['US'],
            standard: {
              enabled: Boolean(form?.shipping?.standard?.enabled),
              label: String(form?.shipping?.standard?.label || ''),
              amount_cents: dollarsToCents(form?.shipping?.standard?.amount_dollars ?? centsToDollars(form?.shipping?.standard?.amount_cents)) ?? 499,
              min_days: Number(form?.shipping?.standard?.min_days ?? 3),
              max_days: Number(form?.shipping?.standard?.max_days ?? 5),
            },
            express: {
              enabled: Boolean(form?.shipping?.express?.enabled),
              label: String(form?.shipping?.express?.label || ''),
              amount_cents: dollarsToCents(form?.shipping?.express?.amount_dollars ?? centsToDollars(form?.shipping?.express?.amount_cents)) ?? 999,
              min_days: Number(form?.shipping?.express?.min_days ?? 1),
              max_days: Number(form?.shipping?.express?.max_days ?? 2),
            },
            free: {
              enabled: Boolean(form?.shipping?.free?.enabled),
              label: String(form?.shipping?.free?.label || ''),
              threshold_cents: dollarsToCents(form?.shipping?.free?.threshold_dollars ?? centsToDollars(form?.shipping?.free?.threshold_cents)) ?? 2000,
              min_days: Number(form?.shipping?.free?.min_days ?? 5),
              max_days: Number(form?.shipping?.free?.max_days ?? 8),
            },
          },
          tax: {
            stripe_automatic_tax_enabled: Boolean(form?.tax?.stripe_automatic_tax_enabled),
          },
          optional_sections: {
            crochet_features: {
              enabled: Boolean(form?.optional_sections?.crochet_features?.enabled),
              title: String(form?.optional_sections?.crochet_features?.title || 'Crochet features'),
              html: String(form?.optional_sections?.crochet_features?.html || ''),
            },
            events: {
              enabled: Boolean(form?.optional_sections?.events?.enabled),
              title: String(form?.optional_sections?.events?.title || 'Events'),
              html: String(form?.optional_sections?.events?.html || ''),
            },
            reviews: {
              enabled: Boolean(form?.optional_sections?.reviews?.enabled),
              title: String(form?.optional_sections?.reviews?.title || 'Reviews'),
              auto_approve: Boolean(form?.optional_sections?.reviews?.auto_approve),
              double_opt_in: true,
            },
          },
        },
      };

      const res = await fetch(`${API_BASE_URL}/admin/general`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Save failed'));

      setForm({ ...fallbackDefaults, ...(data?.settings || form) });
      // The just-saved secret (if any) is now stored server-side; clear the
      // write-only input so it never lingers in the UI.
      setUbereatsSecretInput('');
      setDoordashSecretInput('');
      setSource(data?.source || 'stored');
      const stripeSyncOk = Boolean(data?.stripeSync?.ok);
      const stripeSyncErr = data?.stripeSync?.error;
      if (stripeSyncOk) {
        setStatusMessage('Saved.');
      } else if (stripeSyncErr) {
        setStatusMessage(`Saved. Shipping rates could not be synced: ${stripeSyncErr}`);
      } else {
        setStatusMessage('Saved. Shipping rates could not be synced.');
      }
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        window.scrollTo(0, 0);
      }
      setTimeout(() => {
        try {
          statusRef.current?.focus?.();
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        } catch {
          // ignore
        }
      }, 50);
      setSaving(false);
    }
  };

  const reports = (form?.reports && typeof form.reports === 'object')
    ? form.reports
    : fallbackDefaults.reports;

  const reportTypeOptions = useMemo(() => {
    return [
      { k: 'orders', label: 'Orders' },
      { k: 'labels', label: 'Labels' },
      { k: 'users', label: 'Users' },
      { k: 'fulfillment', label: 'Fulfillment' },
      { k: 'online_sales', label: 'Online Sales' },
    ];
  }, []);

  const enabledReportTypes = useMemo(() => {
    const raw = Array.isArray(reports?.enabledReportTypes) ? reports.enabledReportTypes : fallbackDefaults.reports.enabledReportTypes;
    const allowed = new Set(['orders', 'labels', 'users', 'fulfillment', 'online_sales']);
    const out = [];
    const seen = new Set();
    for (const t of raw || []) {
      const v = String(t || '').trim().toLowerCase();
      if (allowed.has(v) && !seen.has(v)) {
        out.push(v);
        seen.add(v);
      }
    }
    return out.length ? out : ['orders', 'labels', 'users', 'fulfillment', 'online_sales'];
  }, [reports?.enabledReportTypes]);

  if (loading) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading settings...</p>
      </div>
    );
  }

  const standard = form?.shipping?.standard || fallbackDefaults.shipping.standard;

  const express = form?.shipping?.express || fallbackDefaults.shipping.express;
  const free = form?.shipping?.free || fallbackDefaults.shipping.free;
  const orderingClosedHours = (form?.orderingClosedHours && typeof form.orderingClosedHours === 'object')
    ? form.orderingClosedHours
    : fallbackDefaults.orderingClosedHours;
  const serviceWindows = (form?.serviceWindows && typeof form.serviceWindows === 'object')
    ? form.serviceWindows
    : fallbackDefaults.serviceWindows;
  const posDeliveryLinks = (form?.posDeliveryLinks && typeof form.posDeliveryLinks === 'object')
    ? form.posDeliveryLinks
    : fallbackDefaults.posDeliveryLinks;

  const toggleReportType = (key) => {
    const k = String(key || '').trim().toLowerCase();
    if (!k) return;
    const next = enabledReportTypes.includes(k) ? enabledReportTypes.filter((x) => x !== k) : [...enabledReportTypes, k];
    if (!next.length) {
      setError('At least one report type must be enabled');
      return;
    }
    setError('');
    updateReports({ enabledReportTypes: next });
  };

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div>
          <h1>General</h1>
          <p>Shipping & taxes</p>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username || 'Admin'}
          </span>
          <button onClick={() => Auth.signOut().then(() => navigate('/admin/login'))} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        {legacyBanner ? (
          <div className="alert" style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412' }}>
            <i className="fas fa-info-circle" style={{ marginRight: 8 }}></i>
            Currently using legacy defaults: Standard $4.99, Free over $20. Save to start managing shipping here.
          </div>
        ) : null}

        {error ? (
          <div className="alert alert-error" ref={statusRef} tabIndex={-1} role="alert" aria-live="assertive">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        ) : null}

        {statusMessage ? (
          <div className="alert alert-success" ref={statusRef} tabIndex={-1} role="status" aria-live="polite">
            <i className="fas fa-check-circle"></i>
            {statusMessage}
          </div>
        ) : null}

        <div className="products-list-container">
          <details open>
            <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Storefront mode</summary>
            <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)', marginTop: 14 }}>
              <div className="form-row" style={{ gap: 14, alignItems: 'end' }}>
                <div className="form-group" style={{ minWidth: 280 }}>
                  <label>Mode</label>
                  <select value={String(form?.storeMode ?? '')} onChange={(e) => setForm((prev) => ({ ...prev, storeMode: e.target.value }))}>
                    <option value="">Legacy (no mode set)</option>
                    <option value="ECOMM">ECOMM (eCommerce)</option>
                    <option value="POS">POS (In-store ordering)</option>
                    <option value="HYBRID">HYBRID (demo only)</option>
                  </select>
                  <div className="field-hint">This controls which parts of the public app are enabled for this domain.</div>
                </div>

                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const show = mode === 'POS' || mode === 'HYBRID';
                  if (!show) return null;
                  return (
                    <div className="form-group" style={{ minWidth: 240 }}>
                      <label>POS provider</label>
                      <select
                        value={String(form?.posProvider ?? 'SQUARE')}
                        onChange={(e) => setForm((prev) => ({ ...prev, posProvider: e.target.value }))}
                      >
                        <option value="SQUARE">Square</option>
                        <option value="CLOVER">Clover</option>
                        <option value="STRIPE">Stripe</option>
                      </select>
                      <div className="field-hint">Used for POS ordering + payments when Storefront mode is POS/HYBRID.</div>
                    </div>
                  );
                })()}

                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const show = mode === 'POS' || mode === 'HYBRID';
                  if (!show) return null;
                  const onlineEnabled = Boolean(form?.posPayments?.online_enabled ?? true);
                  const inStoreEnabled = Boolean(form?.posPayments?.in_store_enabled ?? true);
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>POS payments</label>
                      <div style={{ display: 'grid', gap: 10 }}>
                        <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                          <input
                            type="checkbox"
                            checked={onlineEnabled}
                            onChange={(e) => {
                              const next = Boolean(e.target.checked);
                              if (!next && !inStoreEnabled) {
                                setError('Cannot disable both online and in-store POS payments');
                                return;
                              }
                              setError('');
                              updatePosPayments({ online_enabled: next });
                            }}
                          />
                          Online payments (Pay now)
                        </label>
                        <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                          <input
                            type="checkbox"
                            checked={inStoreEnabled}
                            onChange={(e) => {
                              const next = Boolean(e.target.checked);
                              if (!next && !onlineEnabled) {
                                setError('Cannot disable both online and in-store POS payments');
                                return;
                              }
                              setError('');
                              updatePosPayments({ in_store_enabled: next });
                            }}
                          />
                          In-store payments (Pay at restaurant)
                        </label>
                      </div>
                      <div className="field-hint">At least one option must remain enabled.</div>
                    </div>
                  );
                })()}

                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const show = mode === 'POS' || mode === 'HYBRID';
                  if (!show) return null;
                  const enabled = Boolean(form?.posPrinting?.autoPrintNewOrders);
                  const detailsOnlyEnabled = Boolean(form?.posPrinting?.orderDetailsOnly);
                  const pickupMessage = String(form?.orderMessages?.pickupMessage || '');
                  const deliveryMessage = String(form?.orderMessages?.deliveryMessage || '');
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>POS printing</label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) => {
                            setError('');
                            updatePosPrinting({ autoPrintNewOrders: Boolean(e.target.checked) });
                          }}
                        />
                        Auto-print new POS queue orders
                      </label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                        <input
                          type="checkbox"
                          checked={detailsOnlyEnabled}
                          onChange={(e) => {
                            setError('');
                            updatePosPrinting({ orderDetailsOnly: Boolean(e.target.checked) });
                          }}
                        />
                        Order Details Only
                      </label>
                      <div className="field-hint">
                        When enabled, the POS Queue page will attempt to print each new incoming order.
                      </div>
                      <div className="field-hint">
                        When Order Details Only is enabled, printing will use a half-letter (5.5 × 8.5) itemized order sheet.
                      </div>

                      {(() => {
                        const slaEnabled = Boolean(form?.posSla?.enabled);
                        const green = form?.posSla?.greenMinutes;
                        const orange = form?.posSla?.orangeMinutes;
                        const red = form?.posSla?.redMinutes;
                        const greenNum = Number(green);
                        const orangeNum = Number(orange);
                        const redNum = Number(red);
                        const ordersValid =
                          Number.isFinite(greenNum) && Number.isFinite(orangeNum) && Number.isFinite(redNum) &&
                          greenNum > 0 && orangeNum > 0 && redNum > 0 &&
                          greenNum < orangeNum && orangeNum < redNum;
                        const showError = slaEnabled && !ordersValid;
                        const onMinChange = (key) => (e) => {
                          setError('');
                          const raw = e.target.value;
                          updatePosSla({ [key]: raw === '' ? '' : Number(raw) });
                        };
                        return (
                          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(148,163,184,0.25)' }}>
                            <label>Order SLA indicators</label>
                            <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                              <input
                                type="checkbox"
                                checked={slaEnabled}
                                onChange={(e) => {
                                  setError('');
                                  updatePosSla({ enabled: Boolean(e.target.checked) });
                                }}
                              />
                              Show SLA blinker lights on OPEN orders
                            </label>
                            <div className="field-hint">
                              For orders still in OPEN/ACTIVE state, the POS Queue shows a blinking light based on
                              minutes elapsed since the order was received: green under the green threshold, orange
                              between green and orange, red at/after the orange threshold.
                            </div>
                            {slaEnabled ? (
                              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>🟢 Green under (min)</span>
                                  <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={green ?? ''}
                                    onChange={onMinChange('greenMinutes')}
                                    style={{ width: 120 }}
                                  />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <span style={{ fontSize: 12, color: '#ea580c', fontWeight: 600 }}>🟠 Orange under (min)</span>
                                  <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={orange ?? ''}
                                    onChange={onMinChange('orangeMinutes')}
                                    style={{ width: 120 }}
                                  />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>🔴 Red at/after (min)</span>
                                  <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={red ?? ''}
                                    onChange={onMinChange('redMinutes')}
                                    style={{ width: 120 }}
                                  />
                                </div>
                              </div>
                            ) : null}
                            {showError ? (
                              <div className="field-hint" style={{ color: '#dc2626', marginTop: 8 }}>
                                Green, orange, and red minutes are all required and must satisfy green &lt; orange &lt; red.
                              </div>
                            ) : null}
                          </div>
                        );
                      })()}

                      <div style={{ marginTop: 20 }}>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
                          Pickup Order Message (Optional)
                        </label>
                        <input
                          type="text"
                          value={pickupMessage}
                          onChange={(e) => {
                            setError('');
                            setForm((prev) => ({
                              ...prev,
                              orderMessages: {
                                ...(prev.orderMessages || {}),
                                pickupMessage: e.target.value,
                              },
                            }));
                          }}
                          placeholder="e.g., Thank you for your order!"
                          style={{ width: '100%', padding: '8px 12px' }}
                        />
                        <div className="field-hint">
                          Optional message displayed on storefront order details page for pickup orders
                        </div>
                      </div>

                      <div style={{ marginTop: 16 }}>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
                          Delivery Order Message (Optional)
                        </label>
                        <input
                          type="text"
                          value={deliveryMessage}
                          onChange={(e) => {
                            setError('');
                            setForm((prev) => ({
                              ...prev,
                              orderMessages: {
                                ...(prev.orderMessages || {}),
                                deliveryMessage: e.target.value,
                              },
                            }));
                          }}
                          placeholder="e.g., Your order will be delivered soon!"
                          style={{ width: '100%', padding: '8px 12px' }}
                        />
                        <div className="field-hint">
                          Optional message displayed on storefront order details page for delivery orders
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const show = mode === 'POS' || mode === 'HYBRID';
                  if (!show) return null;
                  const tipsEnabled = Boolean(form?.posTips?.enabled);
                  const presetPercentages = Array.isArray(form?.posTips?.presetPercentages) 
                    ? form.posTips.presetPercentages 
                    : [15, 18, 20, 25];
                  const allowCustom = Boolean(form?.posTips?.allowCustom ?? true);
                  const allowNoTip = Boolean(form?.posTips?.allowNoTip ?? true);
                  const calculateOn = String(form?.posTips?.calculateOn || 'subtotal');
                  
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>POS Tips</label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={tipsEnabled}
                          onChange={(e) => {
                            setError('');
                            updatePosTips({ enabled: Boolean(e.target.checked) });
                          }}
                        />
                        Enable tips for POS orders
                      </label>
                      
                      {tipsEnabled && (
                        <>
                          <div style={{ marginTop: 12, marginLeft: 24 }}>
                            <label style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 6, display: 'block' }}>
                              Preset tip percentages (comma-separated):
                            </label>
                            <input
                              type="text"
                              value={presetPercentages.join(', ')}
                              onChange={(e) => {
                                const values = e.target.value.split(',').map(v => {
                                  const num = Number(v.trim());
                                  return Number.isFinite(num) && num > 0 ? num : null;
                                }).filter(v => v !== null);
                                updatePosTips({ presetPercentages: values.length ? values : [15, 18, 20, 25] });
                              }}
                              placeholder="15, 18, 20, 25"
                              style={{ width: '100%', maxWidth: 300 }}
                            />
                            <div className="field-hint">Example: 15, 18, 20, 25</div>
                          </div>
                          
                          <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 10, marginLeft: 24 }}>
                            <input
                              type="checkbox"
                              checked={allowCustom}
                              onChange={(e) => {
                                setError('');
                                updatePosTips({ allowCustom: Boolean(e.target.checked) });
                              }}
                            />
                            Allow custom tip amounts
                          </label>
                          
                          <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 10, marginLeft: 24 }}>
                            <input
                              type="checkbox"
                              checked={allowNoTip}
                              onChange={(e) => {
                                setError('');
                                updatePosTips({ allowNoTip: Boolean(e.target.checked) });
                              }}
                            />
                            Allow "No Tip" option
                          </label>
                          
                          <div style={{ marginTop: 12, marginLeft: 24 }}>
                            <label style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 6, display: 'block' }}>
                              Calculate tip on:
                            </label>
                            <select
                              value={calculateOn}
                              onChange={(e) => updatePosTips({ calculateOn: e.target.value })}
                              style={{ width: '100%', maxWidth: 300 }}
                            >
                              <option value="subtotal">Subtotal (before tax)</option>
                              <option value="total_with_tax">Total (after tax)</option>
                            </select>
                          </div>
                        </>
                      )}
                      
                      <div className="field-hint">
                        When enabled, customers will see tip options during POS checkout.
                      </div>
                    </div>
                  );
                })()}

                {(() => {
                  const mode = String(form?.storeMode ?? '').trim().toUpperCase();
                  const show = mode === 'POS' || mode === 'HYBRID';
                  if (!show) return null;
                  const enabled = Boolean(form?.features?.mock_online_payment_enabled);
                  const posDiscountsEnabled = Boolean(form?.features?.pos_discount_codes_enabled);
                  return (
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>Test payment mode</label>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) => updateFeatures({ mock_online_payment_enabled: e.target.checked })}
                        />
                        Enable mock online payment (storefront)
                      </label>
                      <div className="field-hint">
                        When enabled, the storefront will show Pay now as a test payment option.
                      </div>

                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
                        <input
                          type="checkbox"
                          checked={posDiscountsEnabled}
                          onChange={(e) => updateFeatures({ pos_discount_codes_enabled: e.target.checked })}
                        />
                        Enable POS discount codes
                      </label>
                      <div className="field-hint">
                        When enabled, the POS cart will show a discount code field and apply it during Stripe checkout.
                      </div>
                    </div>
                  );
                })()}

                {/* Storefront AI Chatbot — visible for all store modes */}
                <div className="form-group" style={{ minWidth: 280 }}>
                  <label>AI Chatbot</label>
                  <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(form?.features?.chatbot_enabled)}
                      onChange={(e) => updateFeatures({ chatbot_enabled: e.target.checked })}
                    />
                    Enable storefront AI chatbot
                  </label>
                  <div className="field-hint">
                    When enabled, a chat widget appears on the storefront. Requires the provider key to be set in SSM.
                  </div>
                </div>

              </div>
            </section>
          </details>

          <details open style={{ marginTop: 22 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Reports</summary>
            <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)', marginTop: 14 }}>
              <div className="form-row" style={{ gap: 14, alignItems: 'end' }}>
                <div className="form-group" style={{ minWidth: 280 }}>
                  <label>Enabled report types</label>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
                    {reportTypeOptions.map((opt) => (
                      <label
                        key={opt.k}
                        className="toggle-chip"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 10, margin: 0 }}
                      >
                        <input
                          type="checkbox"
                          checked={enabledReportTypes.includes(opt.k)}
                          onChange={() => toggleReportType(opt.k)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                  <div className="field-hint">Controls which report types appear in the Reports dropdown.</div>
                </div>
              </div>
            </section>
          </details>

          {(() => {
            const mode = String(form?.storeMode ?? '').trim().toUpperCase();
            const show = mode === 'POS' || mode === 'HYBRID';
            if (!show) return null;

            const enabled = Boolean(orderingClosedHours?.enabled);
            const tzValue = String(orderingClosedHours?.timezone || 'America/New_York');
            const messageValue = String(orderingClosedHours?.message || '');
            const weekly = Array.isArray(orderingClosedHours?.weekly) ? orderingClosedHours.weekly : [];
            const oneOff = Array.isArray(orderingClosedHours?.oneOff) ? orderingClosedHours.oneOff : [];

            return (
              <details open style={{ marginTop: 22 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Ordering availability (POS)</summary>
                <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)', marginTop: 14 }}>
                  <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => updateOrderingClosedHours({ enabled: Boolean(e.target.checked) })}
                    />
                    Enable scheduled closed hours
                  </label>

                  <div className="form-row" style={{ gap: 14, alignItems: 'end', marginTop: 6 }}>
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>Timezone</label>
                      <select
                        value={tzValue}
                        onChange={(e) => updateOrderingClosedHours({ timezone: e.target.value })}
                        disabled={!enabled}
                      >
                        <option value="America/New_York">Eastern (America/New_York)</option>
                        <option value="America/Chicago">Central (America/Chicago)</option>
                        <option value="America/Denver">Mountain (America/Denver)</option>
                        <option value="America/Phoenix">Arizona (America/Phoenix)</option>
                        <option value="America/Los_Angeles">Pacific (America/Los_Angeles)</option>
                        <option value="America/Anchorage">Alaska (America/Anchorage)</option>
                        <option value="Pacific/Honolulu">Hawaii (Pacific/Honolulu)</option>
                        <option value="Etc/GMT">GMT (Etc/GMT)</option>
                      </select>
                      <div className="field-hint">Used to evaluate scheduled closures.</div>
                    </div>

                    <div className="form-group" style={{ flex: 1, minWidth: 320 }}>
                      <label>Customer message</label>
                      <input
                        value={messageValue}
                        onChange={(e) => updateOrderingClosedHours({ message: e.target.value })}
                        disabled={!enabled}
                        placeholder="We’re currently closed. Please come back during open hours."
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gap: 18, marginTop: 18 }}>
                    <section style={{ border: '1px solid rgba(148, 163, 184, 0.35)', borderRadius: 14, padding: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <h3 style={{ margin: 0 }}>Weekly recurring closures</h3>
                        <button type="button" className="btn-secondary" onClick={addWeeklyClosedRule} disabled={!enabled}>
                          Add
                        </button>
                      </div>

                      {weekly.length === 0 ? (
                        <div className="field-hint" style={{ marginTop: 10 }}>No weekly rules.</div>
                      ) : (
                        <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                          {weekly.map((row) => (
                            <div
                              key={String(row?.id || '')}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 160px 140px 140px 1fr auto',
                                gap: 10,
                                alignItems: 'center',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={Boolean(row?.enabled ?? true)}
                                onChange={(e) => updateWeeklyClosedRule(row?.id, { enabled: Boolean(e.target.checked) })}
                                disabled={!enabled}
                                aria-label="Enable weekly rule"
                              />
                              <select
                                value={String(row?.weekday || 'MON')}
                                onChange={(e) => updateWeeklyClosedRule(row?.id, { weekday: e.target.value })}
                                disabled={!enabled}
                              >
                                <option value="ALL">All days</option>
                                <option value="MON">Mon</option>
                                <option value="TUE">Tue</option>
                                <option value="WED">Wed</option>
                                <option value="THU">Thu</option>
                                <option value="FRI">Fri</option>
                                <option value="SAT">Sat</option>
                                <option value="SUN">Sun</option>
                              </select>
                              <input
                                type="time"
                                value={String(row?.start_hhmm || '09:00')}
                                onChange={(e) => updateWeeklyClosedRule(row?.id, { start_hhmm: e.target.value })}
                                disabled={!enabled}
                              />
                              <input
                                type="time"
                                value={String(row?.end_hhmm || '17:00')}
                                onChange={(e) => updateWeeklyClosedRule(row?.id, { end_hhmm: e.target.value })}
                                disabled={!enabled}
                              />
                              <input
                                value={String(row?.label || '')}
                                onChange={(e) => updateWeeklyClosedRule(row?.id, { label: e.target.value })}
                                disabled={!enabled}
                                placeholder="Label (optional)"
                              />
                              <button type="button" className="btn-secondary" onClick={() => deleteWeeklyClosedRule(row?.id)} disabled={!enabled}>
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="field-hint" style={{ marginTop: 10 }}>
                        Cross-midnight is supported (end time can be earlier than start time).
                      </div>
                    </section>

                    <section style={{ border: '1px solid rgba(148, 163, 184, 0.35)', borderRadius: 14, padding: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <h3 style={{ margin: 0 }}>One-off closures</h3>
                        <button type="button" className="btn-secondary" onClick={addOneOffClosedRule} disabled={!enabled}>
                          Add
                        </button>
                      </div>

                      {oneOff.length === 0 ? (
                        <div className="field-hint" style={{ marginTop: 10 }}>No one-off rules.</div>
                      ) : (
                        <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                          {oneOff.map((row) => (
                            <div
                              key={String(row?.id || '')}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto 240px 240px 1fr auto',
                                gap: 10,
                                alignItems: 'center',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={Boolean(row?.enabled ?? true)}
                                onChange={(e) => updateOneOffClosedRule(row?.id, { enabled: Boolean(e.target.checked) })}
                                disabled={!enabled}
                                aria-label="Enable one-off rule"
                              />
                              <input
                                type="datetime-local"
                                value={String(row?.start_iso || '')}
                                onChange={(e) => updateOneOffClosedRule(row?.id, { start_iso: e.target.value })}
                                disabled={!enabled}
                              />
                              <input
                                type="datetime-local"
                                value={String(row?.end_iso || '')}
                                onChange={(e) => updateOneOffClosedRule(row?.id, { end_iso: e.target.value })}
                                disabled={!enabled}
                              />
                              <input
                                value={String(row?.label || '')}
                                onChange={(e) => updateOneOffClosedRule(row?.id, { label: e.target.value })}
                                disabled={!enabled}
                                placeholder="Label (optional)"
                              />
                              <button type="button" className="btn-secondary" onClick={() => deleteOneOffClosedRule(row?.id)} disabled={!enabled}>
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  </div>
                </section>
              </details>
            );
          })()}

          {(() => {
            const mode = String(form?.storeMode ?? '').trim().toUpperCase();
            const show = mode === 'POS' || mode === 'HYBRID';
            if (!show) return null;

            const enabled = Boolean(serviceWindows?.enabled);
            const tzValue = String(serviceWindows?.timezone || 'America/New_York');
            const windows = Array.isArray(serviceWindows?.windows) ? serviceWindows.windows : [];

            return (
              <details open style={{ marginTop: 22 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Service windows (POS)</summary>
                <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)', marginTop: 14 }}>
                  <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => updateServiceWindows({ enabled: Boolean(e.target.checked) })}
                    />
                    Enable service windows
                  </label>

                  <div className="form-row" style={{ gap: 14, alignItems: 'end', marginTop: 6 }}>
                    <div className="form-group" style={{ minWidth: 280 }}>
                      <label>Timezone</label>
                      <select value={tzValue} onChange={(e) => updateServiceWindows({ timezone: e.target.value })} disabled={!enabled}>
                        <option value="America/New_York">Eastern (America/New_York)</option>
                        <option value="America/Chicago">Central (America/Chicago)</option>
                        <option value="America/Denver">Mountain (America/Denver)</option>
                        <option value="America/Phoenix">Arizona (America/Phoenix)</option>
                        <option value="America/Los_Angeles">Pacific (America/Los_Angeles)</option>
                        <option value="America/Anchorage">Alaska (America/Anchorage)</option>
                        <option value="Pacific/Honolulu">Hawaii (Pacific/Honolulu)</option>
                        <option value="Etc/GMT">GMT (Etc/GMT)</option>
                      </select>
                      <div className="field-hint">Used to determine Breakfast/Lunch/Dinner windows.</div>
                    </div>
                  </div>

                  <section style={{ border: '1px solid rgba(148, 163, 184, 0.35)', borderRadius: 14, padding: 14, marginTop: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                      <h3 style={{ margin: 0 }}>Windows</h3>
                      <button type="button" className="btn-secondary" onClick={addServiceWindowRule} disabled={!enabled}>
                        Add
                      </button>
                    </div>

                    {windows.length === 0 ? (
                      <div className="field-hint" style={{ marginTop: 10 }}>No windows configured.</div>
                    ) : (
                      <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                        {windows.map((row) => {
                          const id = String(row?.id || '');
                          const rowDays = Array.isArray(row?.days) ? row.days : [];
                          const setDays = (days) => updateServiceWindowRule(id, { days });
                          const toggleDay = (day) => {
                            const d = String(day || '').trim().toUpperCase();
                            if (!d) return;
                            if (d === 'ALL') {
                              setDays(['ALL']);
                              return;
                            }
                            const current = rowDays.includes('ALL') ? [] : rowDays;
                            const next = current.includes(d) ? current.filter((x) => x !== d) : [...current, d];
                            setDays(next.length ? next : ['ALL']);
                          };

                          return (
                            <div key={id} style={{ border: '1px solid rgba(148, 163, 184, 0.35)', borderRadius: 14, padding: 12 }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 140px 140px auto', gap: 10, alignItems: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={Boolean(row?.enabled ?? true)}
                                  onChange={(e) => updateServiceWindowRule(id, { enabled: Boolean(e.target.checked) })}
                                  disabled={!enabled}
                                  aria-label="Enable window"
                                />
                                <input
                                  value={String(row?.name || '')}
                                  onChange={(e) => updateServiceWindowRule(id, { name: e.target.value })}
                                  disabled={!enabled}
                                  placeholder="e.g. Breakfast"
                                />
                                <input
                                  type="time"
                                  value={String(row?.start_hhmm || '09:00')}
                                  onChange={(e) => updateServiceWindowRule(id, { start_hhmm: e.target.value })}
                                  disabled={!enabled}
                                />
                                <input
                                  type="time"
                                  value={String(row?.end_hhmm || '17:00')}
                                  onChange={(e) => updateServiceWindowRule(id, { end_hhmm: e.target.value })}
                                  disabled={!enabled}
                                />
                                <button type="button" className="btn-secondary" onClick={() => deleteServiceWindowRule(id)} disabled={!enabled}>
                                  Delete
                                </button>
                              </div>

                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10 }}>
                                {['ALL', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((d) => {
                                  const selected = d === 'ALL' ? rowDays.includes('ALL') || rowDays.length === 0 : (!rowDays.includes('ALL') && rowDays.includes(d));
                                  return (
                                    <label key={`${id}-${d}`} className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, margin: 0, opacity: enabled ? 1 : 0.6 }}>
                                      <input
                                        type="checkbox"
                                        checked={selected}
                                        onChange={() => toggleDay(d)}
                                        disabled={!enabled}
                                      />
                                      {d === 'ALL' ? 'All days' : d}
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="field-hint" style={{ marginTop: 10 }}>Cross-midnight is supported (end time can be earlier than start time).</div>
                  </section>
                </section>
              </details>
            );
          })()}

          {(() => {
            const mode = String(form?.storeMode ?? '').trim().toUpperCase();
            const show = mode === 'POS' || mode === 'HYBRID';
            if (!show) return null;

            const enabled = Boolean(posDeliveryLinks?.enabled);
            const vendors = Array.isArray(posDeliveryLinks?.vendors) ? posDeliveryLinks.vendors : [];

            return (
              <details open style={{ marginTop: 22 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>POS Delivery (External links)</summary>
                <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)', marginTop: 14 }}>
                  <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => updatePosDeliveryLinks({ enabled: Boolean(e.target.checked) })}
                    />
                    Enable Delivery button / external providers
                  </label>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 8 }}>
                    <div style={{ fontWeight: 800 }}>Delivery providers</div>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={addDeliveryVendor}
                      disabled={!enabled}
                    >
                      Add
                    </button>
                  </div>

                  <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
                    {vendors.length ? vendors.map((row) => {
                      const id = row?.id;
                      const preview = String(row?.imageUrl || '') || (typeof row?.imageUpload?.data === 'string' ? row.imageUpload.data : '');
                      return (
                        <div
                          key={String(id || '')}
                          style={{
                            border: '1px solid rgba(148, 163, 184, 0.35)',
                            borderRadius: 14,
                            padding: 12,
                            display: 'grid',
                            gap: 10,
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                            <div style={{ fontWeight: 800 }}>Provider</div>
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => deleteDeliveryVendor(id)}
                              disabled={!enabled}
                            >
                              Delete
                            </button>
                          </div>

                          <div className="form-row" style={{ gap: 12 }}>
                            <div className="form-group" style={{ flex: 1, minWidth: 220 }}>
                              <label>Name</label>
                              <input
                                value={String(row?.name || '')}
                                onChange={(e) => updateDeliveryVendor(id, { name: e.target.value })}
                                disabled={!enabled}
                                placeholder="e.g. DoorDash"
                              />
                            </div>
                            <div className="form-group" style={{ flex: 2, minWidth: 320 }}>
                              <label>Restaurant URL</label>
                              <input
                                value={String(row?.url || '')}
                                onChange={(e) => updateDeliveryVendor(id, { url: e.target.value })}
                                disabled={!enabled}
                                placeholder="https://..."
                              />
                            </div>
                          </div>

                          <div className="form-row" style={{ gap: 12, alignItems: 'center' }}>
                            <div className="form-group" style={{ minWidth: 260 }}>
                              <label>Logo (optional)</label>
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                onChange={(e) => handleDeliveryVendorImageUpload(id, e.target.files?.[0])}
                                disabled={!enabled}
                              />
                            </div>
                            {preview ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                                <img
                                  src={preview}
                                  alt="Vendor logo preview"
                                  style={{ width: 44, height: 44, borderRadius: 12, objectFit: 'cover', border: '1px solid rgba(148, 163, 184, 0.45)' }}
                                />
                                <div style={{ color: 'rgba(15, 23, 42, 0.7)', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {String(row?.imageUrl || '') ? 'Saved logo' : 'New upload (will save on Save)'}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    }) : (
                      <div className="field-hint">No providers added yet.</div>
                    )}
                  </div>
                </section>
              </details>
            );
          })()}

          {/* Internal Delivery Settings */}
          {(() => {
            const mode = String(form?.storeMode || '').trim().toUpperCase();
            const show = mode === 'POS' || mode === 'HYBRID';
            if (!show) return null;

            const delivery = form?.delivery || {};
            const enabled = Boolean(delivery.enabled);
            const feeCents = Number(delivery.fee_cents) || 0;
            const minimumCents = Number(delivery.minimum_order_cents) || 0;
            
            // Radius restriction settings
            const radiusRestriction = delivery.radius_restriction || {};
            const radiusEnabled = Boolean(radiusRestriction.enabled);
            const baseZipCode = String(radiusRestriction.base_zip_code || '').trim();
            const maxRadiusMiles = Number(radiusRestriction.max_radius_miles) || 10.0;

            const updateDelivery = (patch) => {
              setForm((prev) => ({
                ...prev,
                delivery: {
                  enabled: false,
                  fee_cents: 0,
                  minimum_order_cents: 0,
                  radius_restriction: {
                    enabled: false,
                    base_zip_code: '',
                    max_radius_miles: 10.0,
                  },
                  ...((prev.delivery && typeof prev.delivery === 'object') ? prev.delivery : {}),
                  ...(patch || {}),
                },
              }));
            };
            
            const updateRadiusRestriction = (patch) => {
              updateDelivery({
                radius_restriction: {
                  enabled: false,
                  base_zip_code: '',
                  max_radius_miles: 10.0,
                  ...((delivery.radius_restriction && typeof delivery.radius_restriction === 'object') ? delivery.radius_restriction : {}),
                  ...(patch || {}),
                },
              });
            };

            // Address autocomplete is a top-level setting (independent of delivery state).
            // Admin only toggles `enabled`; the Geoapify key is delivered out-of-band.
            const addressAutocomplete = form?.addressAutocomplete || {};
            const autocompleteEnabled = Boolean(addressAutocomplete.enabled);
            const updateAddressAutocomplete = (patch) => {
              setForm((prev) => ({
                ...prev,
                addressAutocomplete: {
                  provider: 'geoapify',
                  enabled: false,
                  ...((prev.addressAutocomplete && typeof prev.addressAutocomplete === 'object') ? prev.addressAutocomplete : {}),
                  ...(patch || {}),
                },
              }));
            };

            return (
              <details open style={{ marginTop: 22 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Internal Delivery</summary>
                <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)', marginTop: 14 }}>
                  <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => updateDelivery({ enabled: Boolean(e.target.checked) })}
                    />
                    Enable internal delivery option
                  </label>

                  <div className="field-hint" style={{ marginBottom: 16, color: 'rgba(15, 23, 42, 0.7)' }}>
                    When enabled, customers can choose delivery at checkout and provide a delivery address. This is separate from external delivery provider links (DoorDash, Uber Eats, etc.).
                  </div>

                  <div className="form-row" style={{ gap: 14 }}>
                    <div className="form-group">
                      <label>Delivery Fee (USD)</label>
                      <PriceInput
                        value={feeCents}
                        onChange={(cents) => updateDelivery({ fee_cents: cents })}
                        disabled={!enabled}
                        placeholder="5.00"
                      />
                      <div className="field-hint">Flat fee added to delivery orders (e.g., $5.00)</div>
                    </div>

                    <div className="form-group">
                      <label>Minimum Order (USD)</label>
                      <PriceInput
                        value={minimumCents}
                        onChange={(cents) => updateDelivery({ minimum_order_cents: cents })}
                        disabled={!enabled}
                        placeholder="10.00"
                      />
                      <div className="field-hint">Minimum subtotal required for delivery (e.g., $10.00). Set to 0 for no minimum.</div>
                    </div>
                  </div>

                  {/* Radius Restriction Configuration */}
                  {enabled && (
                    <div style={{ marginTop: 20, padding: 16, background: 'rgba(99, 102, 241, 0.05)', borderRadius: 8, border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <input
                          type="checkbox"
                          checked={radiusEnabled}
                          onChange={(e) => updateRadiusRestriction({ enabled: Boolean(e.target.checked) })}
                        />
                        <span style={{ fontWeight: 600 }}>Restrict delivery by distance</span>
                      </label>

                      <div className="field-hint" style={{ marginBottom: 16, color: 'rgba(15, 23, 42, 0.7)' }}>
                        When enabled, delivery addresses will be validated against a maximum distance from your store location. Orders outside the radius will be rejected with a helpful message.
                      </div>

                      {radiusEnabled && (
                        <div className="form-row" style={{ gap: 14 }}>
                          <div className="form-group">
                            <label>Store ZIP Code</label>
                            <input
                              type="text"
                              value={baseZipCode}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9-]/g, '').slice(0, 10);
                                updateRadiusRestriction({ base_zip_code: value });
                              }}
                              placeholder="12345"
                              maxLength="10"
                              pattern="[0-9]{5}(-[0-9]{4})?"
                            />
                            <div className="field-hint">Your store's ZIP code (5 digits, e.g., 12345)</div>
                          </div>

                          <div className="form-group">
                            <label>Maximum Delivery Distance (miles)</label>
                            <input
                              type="number"
                              value={maxRadiusMiles}
                              onChange={(e) => {
                                const value = Math.max(0.1, Math.min(100, parseFloat(e.target.value) || 10.0));
                                updateRadiusRestriction({ max_radius_miles: value });
                              }}
                              min="0.1"
                              max="100"
                              step="0.1"
                              placeholder="10.0"
                            />
                            <div className="field-hint">Maximum distance in miles (0.1 to 100 miles)</div>
                          </div>
                        </div>
                      )}

                      {radiusEnabled && baseZipCode && maxRadiusMiles > 0 && (
                        <div style={{ marginTop: 12, padding: 10, background: 'rgba(34, 197, 94, 0.08)', borderRadius: 6, border: '1px solid rgba(34, 197, 94, 0.25)' }}>
                          <div style={{ fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.8)' }}>
                            <strong>Delivery Area:</strong> Within {maxRadiusMiles} miles of ZIP code {baseZipCode}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: 'rgba(15, 23, 42, 0.6)', marginTop: 4 }}>
                            Customers outside this area will see: "This address is X miles from our location. We only deliver within {maxRadiusMiles} miles."
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Address Autocomplete toggle (independent of delivery enablement) */}
                  <div style={{ marginTop: 20, padding: 16, background: 'rgba(99, 102, 241, 0.05)', borderRadius: 8, border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                    <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <input
                        type="checkbox"
                        checked={autocompleteEnabled}
                        onChange={(e) => updateAddressAutocomplete({ enabled: Boolean(e.target.checked) })}
                      />
                      <span style={{ fontWeight: 600 }}>Enable address autocomplete</span>
                    </label>

                    <div className="field-hint" style={{ color: 'rgba(15, 23, 42, 0.7)' }}>
                      When enabled, the delivery Street Address field suggests verified US addresses as the customer types (powered by Geoapify). Requires the Geoapify key to be configured for this tenant.
                    </div>
                  </div>

                  {enabled && (
                    <div style={{ marginTop: 16, padding: 12, background: 'rgba(23, 162, 184, 0.08)', borderRadius: 8, border: '1px solid rgba(23, 162, 184, 0.25)' }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, color: '#137484' }}>Preview</div>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(15, 23, 42, 0.8)' }}>
                        Customers will see: <strong>"Delivery {feeCents > 0 ? `(+$${(feeCents / 100).toFixed(2)})` : '(Free)'}"</strong>
                        {minimumCents > 0 && (
                          <div style={{ marginTop: 4 }}>
                            Minimum order: <strong>${(minimumCents / 100).toFixed(2)}</strong>
                          </div>
                        )}
                        {radiusEnabled && baseZipCode && (
                          <div style={{ marginTop: 4 }}>
                            Delivery area: <strong>Within {maxRadiusMiles} miles of {baseZipCode}</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </section>
              </details>
            );
          })()}

          {/* Delivery Integrations enablement section (Uber Eats, Otter, ...) */}
          {(() => {
            const di = (form?.delivery_integrations && typeof form.delivery_integrations === 'object')
              ? form.delivery_integrations
              : {};
            const ue = (di.ubereats && typeof di.ubereats === 'object') ? di.ubereats : {};

            const ueEnabled = Boolean(ue.enabled);
            const ueEnvironment = ue.environment === 'production' ? 'production' : 'sandbox';
            const ueClientId = String(ue.client_id || '').trim();
            const ueStoreId = String(ue.store_id || '').trim();
            const ueHasSecret = Boolean(ue.has_secret);
            const ueLastSync = ue.last_menu_sync_timestamp;
            const typedSecret = String(ubereatsSecretInput || '').trim();
            // A secret is "available" if one is already stored or the admin
            // typed a new one in this session.
            const ueHasUsableSecret = ueHasSecret || Boolean(typedSecret);

            // DoorDash provider (mirrors Uber Eats; JWT credential model).
            const dd = (di.doordash && typeof di.doordash === 'object') ? di.doordash : {};
            const ddEnabled = Boolean(dd.enabled);
            const ddEnvironment = dd.environment === 'production' ? 'production' : 'sandbox';
            const ddDeveloperId = String(dd.developer_id || '').trim();
            const ddKeyId = String(dd.key_id || '').trim();
            const ddStoreId = String(dd.store_id || '').trim();
            const ddHasSecret = Boolean(dd.has_secret);
            const ddLastSync = dd.last_menu_sync_timestamp;
            const typedDoordashSecret = String(doordashSecretInput || '').trim();
            // A signing secret is "available" if one is already stored or the
            // admin typed a new one in this session.
            const ddHasUsableSecret = ddHasSecret || Boolean(typedDoordashSecret);

            // Otter enablement toggle is relocated here. The detailed Otter
            // config (fields, menu sync) remains in its own section below.
            // `form.otter.enabled` stays the source of truth so toggling in
            // either place stays consistent.
            const otterEnabled = Boolean(form?.otter?.enabled ?? di?.otter?.enabled);

            const updateUberEats = (patch) => {
              setForm((prev) => {
                const prevDi = (prev.delivery_integrations && typeof prev.delivery_integrations === 'object')
                  ? prev.delivery_integrations
                  : {};
                const prevUe = (prevDi.ubereats && typeof prevDi.ubereats === 'object') ? prevDi.ubereats : {};
                return {
                  ...prev,
                  delivery_integrations: {
                    ...prevDi,
                    ubereats: {
                      ...fallbackDefaults.delivery_integrations.ubereats,
                      ...prevUe,
                      ...(patch || {}),
                    },
                  },
                };
              });
            };

            const updateDeliveryOtterEnabled = (next) => {
              const enabledVal = Boolean(next);
              setForm((prev) => {
                const prevDi = (prev.delivery_integrations && typeof prev.delivery_integrations === 'object')
                  ? prev.delivery_integrations
                  : {};
                return {
                  ...prev,
                  // Keep the relocated toggle and the existing top-level Otter
                  // block in sync so existing Otter behavior is preserved.
                  otter: {
                    ...fallbackDefaults.otter,
                    ...((prev.otter && typeof prev.otter === 'object') ? prev.otter : {}),
                    enabled: enabledVal,
                  },
                  delivery_integrations: {
                    ...prevDi,
                    otter: {
                      ...((prevDi.otter && typeof prevDi.otter === 'object') ? prevDi.otter : {}),
                      enabled: enabledVal,
                    },
                  },
                };
              });
            };

            const handleUberEatsTestConnection = async () => {
              if (!ueClientId || !ueStoreId || !ueHasUsableSecret) {
                setError('Please provide client ID, client secret, and store ID before testing the Uber Eats connection');
                setUeTestResult({ ok: false, message: 'Provide client ID, client secret, and store ID before testing.' });
                return;
              }
              setStatusMessage('Testing Uber Eats connection...');
              setError('');
              setUeTestResult({ ok: null, message: 'Testing Uber Eats connection...' });
              try {
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();
                const body = {
                  environment: ueEnvironment,
                  client_id: ueClientId,
                  store_id: ueStoreId,
                };
                // Only include the secret if the admin typed a new one this
                // session; otherwise the backend uses the stored secret.
                if (typedSecret) body.client_secret = typedSecret;

                const response = await fetch(`${API_BASE_URL}/admin/ubereats/connection/test`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify(body),
                });
                const data = await response.json().catch(() => ({}));
                if (response.ok && data.success) {
                  const msg = data.message || 'Uber Eats connection successful!';
                  setStatusMessage(msg);
                  setUeTestResult({ ok: true, message: msg });
                  setTimeout(() => setStatusMessage(''), 5000);
                } else {
                  const msg = data.error || data.message || 'Uber Eats connection test failed';
                  setError(msg);
                  setStatusMessage('');
                  setUeTestResult({ ok: false, message: msg });
                }
              } catch (err) {
                console.error('Uber Eats connection test error:', err);
                const msg = `Uber Eats connection test failed: ${err.message}`;
                setError(msg);
                setStatusMessage('');
                setUeTestResult({ ok: false, message: msg });
              }
            };

            const handleUberEatsMenuSync = async () => {
              if (!ueEnabled) {
                setError('Please enable Uber Eats integration first');
                return;
              }
              setStatusMessage('Syncing menu with Uber Eats...');
              setError('');
              try {
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();

                // Optional selective sync: parse the comma-separated Product IDs
                // input. When empty, omit product_ids so the backend syncs the
                // full catalog (existing behavior).
                const selectedProductIds = String(ueSyncProductIds || '')
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean);
                const requestBody = selectedProductIds.length
                  ? { product_ids: selectedProductIds }
                  : {};

                const response = await fetch(`${API_BASE_URL}/admin/ubereats/menu/sync`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify(requestBody),
                });
                const data = await response.json().catch(() => ({}));
                if (response.ok) {
                  const itemsCount = data.items_count || 0;
                  const categoriesCount = data.categories_count || 0;
                  setStatusMessage(`Uber Eats menu synced! ${itemsCount} items, ${categoriesCount} categories`);
                  updateUberEats({ last_menu_sync_timestamp: data.last_menu_sync_timestamp || Date.now() });
                  setTimeout(() => setStatusMessage(''), 5000);
                } else {
                  setError(data.error || data.details || 'Uber Eats menu sync failed');
                  setStatusMessage('');
                }
              } catch (err) {
                console.error('Uber Eats menu sync error:', err);
                setError(`Uber Eats menu sync failed: ${err.message}`);
                setStatusMessage('');
              }
            };

            const updateDoorDash = (patch) => {
              setForm((prev) => {
                const prevDi = (prev.delivery_integrations && typeof prev.delivery_integrations === 'object')
                  ? prev.delivery_integrations
                  : {};
                const prevDd = (prevDi.doordash && typeof prevDi.doordash === 'object') ? prevDi.doordash : {};
                return {
                  ...prev,
                  delivery_integrations: {
                    ...prevDi,
                    doordash: {
                      ...fallbackDefaults.delivery_integrations.doordash,
                      ...prevDd,
                      ...(patch || {}),
                    },
                  },
                };
              });
            };

            const handleDoorDashTestConnection = async () => {
              if (!ddDeveloperId || !ddKeyId || !ddStoreId || !ddHasUsableSecret) {
                setError('Please provide developer ID, key ID, signing secret, and store ID before testing the DoorDash connection');
                return;
              }
              setStatusMessage('Testing DoorDash connection...');
              setError('');
              try {
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();
                const body = {
                  environment: ddEnvironment,
                  developer_id: ddDeveloperId,
                  key_id: ddKeyId,
                  store_id: ddStoreId,
                };
                // Only include the secret if the admin typed a new one this
                // session; otherwise the backend uses the stored secret.
                if (typedDoordashSecret) body.signing_secret = typedDoordashSecret;

                const response = await fetch(`${API_BASE_URL}/admin/doordash/connection/test`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify(body),
                });
                const data = await response.json().catch(() => ({}));
                if (response.ok && data.success) {
                  setStatusMessage(data.message || 'DoorDash connection successful!');
                  setTimeout(() => setStatusMessage(''), 5000);
                } else {
                  setError(data.error || data.message || 'DoorDash connection test failed');
                  setStatusMessage('');
                }
              } catch (err) {
                console.error('DoorDash connection test error:', err);
                setError(`DoorDash connection test failed: ${err.message}`);
                setStatusMessage('');
              }
            };

            const handleDoorDashMenuSync = async () => {
              if (!ddEnabled) {
                setError('Please enable DoorDash integration first');
                return;
              }
              setStatusMessage('Syncing menu with DoorDash...');
              setError('');
              try {
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();
                const response = await fetch(`${API_BASE_URL}/admin/doordash/menu/sync`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                });
                const data = await response.json().catch(() => ({}));
                if (response.ok) {
                  const itemsCount = data.items_count || 0;
                  const categoriesCount = data.categories_count || 0;
                  setStatusMessage(`DoorDash menu synced! ${itemsCount} items, ${categoriesCount} categories`);
                  updateDoorDash({ last_menu_sync_timestamp: data.last_menu_sync_timestamp || Date.now() });
                  setTimeout(() => setStatusMessage(''), 5000);
                } else {
                  setError(data.error || data.details || 'DoorDash menu sync failed');
                  setStatusMessage('');
                }
              } catch (err) {
                console.error('DoorDash menu sync error:', err);
                setError(`DoorDash menu sync failed: ${err.message}`);
                setStatusMessage('');
              }
            };

            const providerCardStyle = {
              background: '#fff',
              borderRadius: 12,
              padding: 16,
              border: '1px solid rgba(148, 163, 184, 0.35)',
              marginTop: 14,
            };
            const buttonStyle = (disabled, color) => ({
              padding: '8px 16px',
              borderRadius: 6,
              border: `1px solid ${color}80`,
              background: `${color}1a`,
              color,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
            });

            return (
              <details open style={{ marginTop: 22 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Delivery Integrations</summary>

                <div className="field-hint" style={{ marginTop: 10, marginBottom: 4, color: 'rgba(15, 23, 42, 0.7)' }}>
                  Enable or disable each delivery integration independently. All integrations are off by default. Disabling an integration preserves its saved credentials.
                </div>

                {/* Uber Eats provider */}
                <section style={providerCardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#1e293b' }}>🚗 Uber Eats</h3>
                    <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={ueEnabled}
                        onChange={(e) => updateUberEats({ enabled: Boolean(e.target.checked) })}
                      />
                      {ueEnabled ? 'Enabled' : 'Disabled'}
                    </label>
                  </div>

                  <div className="field-hint" style={{ marginTop: 8, marginBottom: 12, color: 'rgba(15, 23, 42, 0.7)' }}>
                    Direct integration with the Uber Eats Marketplace API. Orders flow into your admin order queue. Use the sandbox environment for testing before going live.
                  </div>

                  <div className="form-row" style={{ gap: 14 }}>
                    <div className="form-group">
                      <label>Environment</label>
                      <select
                        value={ueEnvironment}
                        onChange={(e) => updateUberEats({ environment: e.target.value === 'production' ? 'production' : 'sandbox' })}
                        disabled={!ueEnabled}
                        style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid rgba(148, 163, 184, 0.35)' }}
                      >
                        <option value="sandbox">Sandbox (testing)</option>
                        <option value="production">Production (live)</option>
                      </select>
                      <div className="field-hint">Sandbox routes to test-api.uber.com; Production routes to api.uber.com.</div>
                    </div>

                    <div className="form-group">
                      <label>Store ID *</label>
                      <input
                        type="text"
                        value={ueStoreId}
                        onChange={(e) => updateUberEats({ store_id: e.target.value })}
                        disabled={!ueEnabled}
                        placeholder="Enter Uber Eats store ID"
                        maxLength="100"
                      />
                      <div className="field-hint">Your Uber Eats store identifier.</div>
                    </div>
                  </div>

                  <div className="form-row" style={{ gap: 14 }}>
                    <div className="form-group">
                      <label>Client ID *</label>
                      <input
                        type="text"
                        value={ueClientId}
                        onChange={(e) => updateUberEats({ client_id: e.target.value })}
                        disabled={!ueEnabled}
                        placeholder="Enter Uber Eats client ID"
                        maxLength="200"
                      />
                      <div className="field-hint">OAuth client ID from your Uber developer app.</div>
                    </div>

                    <div className="form-group">
                      <label>Client Secret *</label>
                      <input
                        type="password"
                        value={ubereatsSecretInput}
                        onChange={(e) => setUbereatsSecretInput(e.target.value)}
                        disabled={!ueEnabled}
                        placeholder={ueHasSecret ? '•••••••• (secret saved — leave blank to keep)' : 'Enter Uber Eats client secret'}
                        maxLength="500"
                        autoComplete="new-password"
                      />
                      <div className="field-hint">
                        {ueHasSecret
                          ? 'A secret is already saved. Leave blank to keep it, or type a new value to replace it.'
                          : 'OAuth client secret. Stored securely and never displayed after saving.'}
                      </div>
                    </div>
                  </div>

                  {ueEnabled && (
                    <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={handleUberEatsTestConnection}
                        disabled={!ueClientId || !ueStoreId || !ueHasUsableSecret}
                        style={buttonStyle(!ueClientId || !ueStoreId || !ueHasUsableSecret, '#4c1d95')}
                      >
                        Test Connection
                      </button>
                      <button
                        type="button"
                        onClick={handleUberEatsMenuSync}
                        style={buttonStyle(false, '#15803d')}
                      >
                        Sync Menu Now
                      </button>
                      {ueLastSync && (
                        <span style={{ fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.6)' }}>
                          Last menu sync: {new Date(ueLastSync).toLocaleString()}
                        </span>
                      )}
                    </div>
                  )}

                  {ueEnabled && (
                    <div style={{ marginTop: 10 }}>
                      <label
                        htmlFor="ueSyncProductIds"
                        style={{ display: 'block', fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.7)', marginBottom: 4 }}
                      >
                        Product IDs to sync (optional)
                      </label>
                      <input
                        id="ueSyncProductIds"
                        type="text"
                        value={ueSyncProductIds}
                        onChange={(e) => setUeSyncProductIds(e.target.value)}
                        placeholder="Comma-separated Product IDs. Leave blank to sync the entire menu."
                        style={{ width: '100%', maxWidth: 520, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(15, 23, 42, 0.2)' }}
                      />
                      <div style={{ fontSize: '0.8rem', color: 'rgba(15, 23, 42, 0.55)', marginTop: 4 }}>
                        Leave empty to sync all products. Enter specific Product IDs (comma-separated) to sync only those.
                      </div>
                    </div>
                  )}

                  {ueEnabled && ueTestResult && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: 12,
                        borderRadius: 8,
                        background: ueTestResult.ok === true
                          ? 'rgba(34, 197, 94, 0.10)'
                          : ueTestResult.ok === false
                            ? 'rgba(239, 68, 68, 0.08)'
                            : 'rgba(100, 116, 139, 0.10)',
                        border: ueTestResult.ok === true
                          ? '1px solid rgba(34, 197, 94, 0.30)'
                          : ueTestResult.ok === false
                            ? '1px solid rgba(239, 68, 68, 0.25)'
                            : '1px solid rgba(100, 116, 139, 0.25)',
                        color: ueTestResult.ok === true
                          ? '#15803d'
                          : ueTestResult.ok === false
                            ? '#dc2626'
                            : 'rgba(15, 23, 42, 0.8)',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                      }}
                    >
                      {ueTestResult.ok === true ? '✓ ' : ueTestResult.ok === false ? '⚠ ' : '… '}
                      {ueTestResult.message}
                    </div>
                  )}

                  {ueEnabled && (!ueClientId || !ueStoreId || !ueHasUsableSecret) && (
                    <div style={{ marginTop: 16, padding: 12, background: 'rgba(239, 68, 68, 0.08)', borderRadius: 8, border: '1px solid rgba(239, 68, 68, 0.25)' }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, color: '#dc2626' }}>⚠ Configuration Incomplete</div>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(15, 23, 42, 0.8)' }}>
                        Provide client ID, client secret, and store ID to complete the Uber Eats integration.
                      </div>
                    </div>
                  )}
                </section>

                {/* DoorDash provider */}
                <section style={providerCardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#1e293b' }}>🛵 DoorDash</h3>
                    <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={ddEnabled}
                        onChange={(e) => updateDoorDash({ enabled: Boolean(e.target.checked) })}
                      />
                      {ddEnabled ? 'Enabled' : 'Disabled'}
                    </label>
                  </div>

                  <div className="field-hint" style={{ marginTop: 8, marginBottom: 12, color: 'rgba(15, 23, 42, 0.7)' }}>
                    Direct integration with the DoorDash Marketplace API. Orders flow into your admin order queue. Use the sandbox environment for testing before going live.
                  </div>

                  <div className="form-row" style={{ gap: 14 }}>
                    <div className="form-group">
                      <label>Environment</label>
                      <select
                        value={ddEnvironment}
                        onChange={(e) => updateDoorDash({ environment: e.target.value === 'production' ? 'production' : 'sandbox' })}
                        disabled={!ddEnabled}
                        style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid rgba(148, 163, 184, 0.35)' }}
                      >
                        <option value="sandbox">Sandbox (testing)</option>
                        <option value="production">Production (live)</option>
                      </select>
                      <div className="field-hint">Sandbox routes to the DoorDash sandbox; Production routes to the live DoorDash API.</div>
                    </div>

                    <div className="form-group">
                      <label>Store ID *</label>
                      <input
                        type="text"
                        value={ddStoreId}
                        onChange={(e) => updateDoorDash({ store_id: e.target.value })}
                        disabled={!ddEnabled}
                        placeholder="Enter DoorDash store ID"
                        maxLength="100"
                      />
                      <div className="field-hint">Your DoorDash store identifier.</div>
                    </div>
                  </div>

                  <div className="form-row" style={{ gap: 14 }}>
                    <div className="form-group">
                      <label>Developer ID *</label>
                      <input
                        type="text"
                        value={ddDeveloperId}
                        onChange={(e) => updateDoorDash({ developer_id: e.target.value })}
                        disabled={!ddEnabled}
                        placeholder="Enter DoorDash developer ID"
                        maxLength="200"
                      />
                      <div className="field-hint">Developer ID from your DoorDash Developer Portal credentials.</div>
                    </div>

                    <div className="form-group">
                      <label>Key ID *</label>
                      <input
                        type="text"
                        value={ddKeyId}
                        onChange={(e) => updateDoorDash({ key_id: e.target.value })}
                        disabled={!ddEnabled}
                        placeholder="Enter DoorDash key ID"
                        maxLength="200"
                      />
                      <div className="field-hint">Key ID used to sign the request JWT.</div>
                    </div>
                  </div>

                  <div className="form-row" style={{ gap: 14 }}>
                    <div className="form-group">
                      <label>Signing Secret *</label>
                      <input
                        type="password"
                        value={doordashSecretInput}
                        onChange={(e) => setDoordashSecretInput(e.target.value)}
                        disabled={!ddEnabled}
                        placeholder={ddHasSecret ? '•••••••• (secret saved — leave blank to keep)' : 'Enter DoorDash signing secret'}
                        maxLength="500"
                        autoComplete="new-password"
                      />
                      <div className="field-hint">
                        {ddHasSecret
                          ? 'A secret is already saved. Leave blank to keep it, or type a new value to replace it.'
                          : 'JWT signing secret. Stored securely and never displayed after saving.'}
                      </div>
                    </div>
                  </div>

                  {ddEnabled && (
                    <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={handleDoorDashTestConnection}
                        disabled={!ddDeveloperId || !ddKeyId || !ddStoreId || !ddHasUsableSecret}
                        style={buttonStyle(!ddDeveloperId || !ddKeyId || !ddStoreId || !ddHasUsableSecret, '#4c1d95')}
                      >
                        Test Connection
                      </button>
                      <button
                        type="button"
                        onClick={handleDoorDashMenuSync}
                        style={buttonStyle(false, '#15803d')}
                      >
                        Sync Menu Now
                      </button>
                      {ddLastSync && (
                        <span style={{ fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.6)' }}>
                          Last menu sync: {new Date(ddLastSync).toLocaleString()}
                        </span>
                      )}
                    </div>
                  )}

                  {ddEnabled && (!ddDeveloperId || !ddKeyId || !ddStoreId || !ddHasUsableSecret) && (
                    <div style={{ marginTop: 16, padding: 12, background: 'rgba(239, 68, 68, 0.08)', borderRadius: 8, border: '1px solid rgba(239, 68, 68, 0.25)' }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, color: '#dc2626' }}>⚠ Configuration Incomplete</div>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(15, 23, 42, 0.8)' }}>
                        Provide developer ID, key ID, signing secret, and store ID to complete the DoorDash integration.
                      </div>
                    </div>
                  )}
                </section>

                {/* Otter provider — enablement toggle only (detailed config below) */}
                <section style={providerCardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#1e293b' }}>🦦 Otter</h3>
                    <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={otterEnabled}
                        onChange={(e) => updateDeliveryOtterEnabled(e.target.checked)}
                      />
                      {otterEnabled ? 'Enabled' : 'Disabled'}
                    </label>
                  </div>
                  <div className="field-hint" style={{ marginTop: 8, color: 'rgba(15, 23, 42, 0.7)' }}>
                    Aggregates orders from multiple delivery platforms. Configure Otter credentials and menu sync in the Otter Delivery Integration section below.
                  </div>
                </section>
              </details>
            );
          })()}

          {/* Otter Delivery Integration Settings */}
          {(() => {
            const mode = String(form?.storeMode || '').trim().toUpperCase();
            const show = mode === 'POS' || mode === 'HYBRID';
            if (!show) return null;

            const otter = form?.otter || {};
            const enabled = Boolean(otter.enabled);
            const apiKey = String(otter.api_key || '').trim();
            const apiSecret = String(otter.api_secret || '').trim();
            const storeId = String(otter.store_id || '').trim();
            const webhookUrl = String(otter.webhook_url || '').trim();
            const menuSyncEnabled = Boolean(otter.menu_sync_enabled);
            const menuSyncMode = String(otter.menu_sync_mode || 'manual').trim();
            const lastSyncTimestamp = otter.last_menu_sync_timestamp;

            const updateOtter = (patch) => {
              setForm((prev) => ({
                ...prev,
                otter: {
                  enabled: false,
                  api_key: '',
                  api_secret: '',
                  store_id: '',
                  webhook_url: '',
                  menu_sync_enabled: false,
                  menu_sync_mode: 'manual',
                  last_menu_sync_timestamp: null,
                  ...((prev.otter && typeof prev.otter === 'object') ? prev.otter : {}),
                  ...(patch || {}),
                },
              }));
            };

            const handleTestConnection = async () => {
              if (!apiKey || !apiSecret || !storeId) {
                setError('Please provide API key, API secret, and store ID before testing connection');
                return;
              }
              
              setStatusMessage('Testing Otter connection...');
              setError('');
              
              try {
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();
                
                const response = await fetch(`${API_BASE_URL}/admin/otter/connection/test`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({
                    api_key: apiKey,
                    api_secret: apiSecret,
                    store_id: storeId,
                  }),
                });
                
                const data = await response.json();
                
                if (response.ok && data.success) {
                  setStatusMessage(data.message || 'Connection successful!');
                  // Clear success message after 5 seconds
                  setTimeout(() => setStatusMessage(''), 5000);
                } else {
                  const errorMsg = data.error || 'Connection test failed';
                  setError(errorMsg);
                  setStatusMessage('');
                }
              } catch (err) {
                console.error('Connection test error:', err);
                setError(`Connection test failed: ${err.message}`);
                setStatusMessage('');
              }
            };

            const handleMenuSync = async () => {
              if (!enabled) {
                setError('Please enable Otter integration first');
                return;
              }
              
              setStatusMessage('Syncing menu with Otter...');
              setError('');
              
              try {
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();
                
                const response = await fetch(`${API_BASE_URL}/admin/otter/menu/sync`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                });
                
                const data = await response.json();
                
                if (response.ok) {
                  const itemsCount = data.items_count || 0;
                  const categoriesCount = data.categories_count || 0;
                  const duration = data.total_duration_seconds || 0;
                  
                  setStatusMessage(`Menu synced successfully! ${itemsCount} items, ${categoriesCount} categories (${duration}s)`);
                  
                  // Update last sync timestamp
                  updateOtter({ last_menu_sync_timestamp: Date.now() });
                  
                  // Clear success message after 5 seconds
                  setTimeout(() => setStatusMessage(''), 5000);
                } else {
                  const errorMsg = data.error || data.details || 'Menu sync failed';
                  setError(errorMsg);
                  setStatusMessage('');
                }
              } catch (err) {
                console.error('Menu sync error:', err);
                setError(`Menu sync failed: ${err.message}`);
                setStatusMessage('');
              }
            };

            const openOtterDashboard = () => {
              if (storeId) {
                window.open(`https://dashboard.otter.com/store/${storeId}`, '_blank');
              } else {
                setError('Please provide store ID to open Otter dashboard');
              }
            };

            return (
              <details open style={{ marginTop: 22 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Otter Delivery Integration</summary>
                <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)', marginTop: 14 }}>
                  <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => updateOtter({ enabled: Boolean(e.target.checked) })}
                    />
                    Enable Otter integration
                  </label>

                  <div className="field-hint" style={{ marginBottom: 16, color: 'rgba(15, 23, 42, 0.7)' }}>
                    Otter consolidates orders from multiple delivery platforms (DoorDash, Uber Eats, GrubHub, etc.) into your admin queue. Customers continue using their preferred delivery apps.
                  </div>

                  <div className="form-row" style={{ gap: 14 }}>
                    <div className="form-group">
                      <label>API Key *</label>
                      <input
                        type="text"
                        value={apiKey}
                        onChange={(e) => updateOtter({ api_key: e.target.value })}
                        disabled={!enabled}
                        placeholder="Enter Otter API key"
                        maxLength="200"
                      />
                      <div className="field-hint">Your Otter API key for authentication</div>
                    </div>

                    <div className="form-group">
                      <label>API Secret *</label>
                      <input
                        type="password"
                        value={apiSecret}
                        onChange={(e) => updateOtter({ api_secret: e.target.value })}
                        disabled={!enabled}
                        placeholder="Enter Otter API secret"
                        maxLength="200"
                      />
                      <div className="field-hint">Your Otter API secret for webhook signature verification</div>
                    </div>
                  </div>

                  <div className="form-row" style={{ gap: 14 }}>
                    <div className="form-group">
                      <label>Store ID *</label>
                      <input
                        type="text"
                        value={storeId}
                        onChange={(e) => updateOtter({ store_id: e.target.value })}
                        disabled={!enabled}
                        placeholder="Enter Otter store ID"
                        maxLength="100"
                      />
                      <div className="field-hint">Your unique Otter store identifier</div>
                    </div>

                    <div className="form-group">
                      <label>Webhook URL (Reference)</label>
                      <input
                        type="text"
                        value={webhookUrl}
                        onChange={(e) => updateOtter({ webhook_url: e.target.value })}
                        disabled={!enabled}
                        placeholder="https://api.yourdomain.com/marketplace/otter/webhook"
                        maxLength="500"
                      />
                      <div className="field-hint">Configure this URL in your Otter dashboard</div>
                    </div>
                  </div>

                  {enabled && (
                    <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
                      <button
                        type="button"
                        onClick={handleTestConnection}
                        disabled={!apiKey || !apiSecret || !storeId}
                        style={{
                          padding: '8px 16px',
                          borderRadius: 6,
                          border: '1px solid rgba(99, 102, 241, 0.5)',
                          background: 'rgba(99, 102, 241, 0.1)',
                          color: '#4c1d95',
                          cursor: (!apiKey || !apiSecret || !storeId) ? 'not-allowed' : 'pointer',
                          opacity: (!apiKey || !apiSecret || !storeId) ? 0.5 : 1,
                        }}
                      >
                        Test Connection
                      </button>
                      <button
                        type="button"
                        onClick={openOtterDashboard}
                        disabled={!storeId}
                        style={{
                          padding: '8px 16px',
                          borderRadius: 6,
                          border: '1px solid rgba(99, 102, 241, 0.5)',
                          background: 'rgba(99, 102, 241, 0.1)',
                          color: '#4c1d95',
                          cursor: !storeId ? 'not-allowed' : 'pointer',
                          opacity: !storeId ? 0.5 : 1,
                        }}
                      >
                        Open Otter Dashboard
                      </button>
                    </div>
                  )}

                  {/* Menu Sync Configuration */}
                  {enabled && (
                    <div style={{ marginTop: 20, padding: 16, background: 'rgba(99, 102, 241, 0.05)', borderRadius: 8, border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                      <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <input
                          type="checkbox"
                          checked={menuSyncEnabled}
                          onChange={(e) => updateOtter({ menu_sync_enabled: Boolean(e.target.checked) })}
                        />
                        <span style={{ fontWeight: 600 }}>Enable menu synchronization</span>
                      </label>

                      <div className="field-hint" style={{ marginBottom: 16, color: 'rgba(15, 23, 42, 0.7)' }}>
                        Automatically sync your menu data with Otter so delivery platforms display current items and prices.
                      </div>

                      {menuSyncEnabled && (
                        <>
                          <div className="form-group" style={{ marginBottom: 16 }}>
                            <label>Sync Mode</label>
                            <select
                              value={menuSyncMode}
                              onChange={(e) => updateOtter({ menu_sync_mode: e.target.value })}
                              style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid rgba(148, 163, 184, 0.35)' }}
                            >
                              <option value="manual">Manual - Sync when you click the button</option>
                              <option value="automatic">Automatic - Sync every 5 minutes</option>
                            </select>
                            <div className="field-hint">Choose how often to sync your menu with Otter</div>
                          </div>

                          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            <button
                              type="button"
                              onClick={handleMenuSync}
                              style={{
                                padding: '8px 16px',
                                borderRadius: 6,
                                border: '1px solid rgba(34, 197, 94, 0.5)',
                                background: 'rgba(34, 197, 94, 0.1)',
                                color: '#15803d',
                                cursor: 'pointer',
                              }}
                            >
                              Sync Menu Now
                            </button>
                            {lastSyncTimestamp && (
                              <span style={{ fontSize: '0.85rem', color: 'rgba(15, 23, 42, 0.6)' }}>
                                Last synced: {new Date(lastSyncTimestamp).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {enabled && apiKey && apiSecret && storeId && (
                    <div style={{ marginTop: 16, padding: 12, background: 'rgba(34, 197, 94, 0.08)', borderRadius: 8, border: '1px solid rgba(34, 197, 94, 0.25)' }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, color: '#15803d' }}>✓ Configuration Complete</div>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(15, 23, 42, 0.8)' }}>
                        Otter orders from DoorDash, Uber Eats, GrubHub, and other platforms will appear in your admin order queue.
                      </div>
                    </div>
                  )}

                  {enabled && (!apiKey || !apiSecret || !storeId) && (
                    <div style={{ marginTop: 16, padding: 12, background: 'rgba(239, 68, 68, 0.08)', borderRadius: 8, border: '1px solid rgba(239, 68, 68, 0.25)' }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, color: '#dc2626' }}>⚠ Configuration Incomplete</div>
                      <div style={{ fontSize: '0.9rem', color: 'rgba(15, 23, 42, 0.8)' }}>
                        Please provide API key, API secret, and store ID to complete the integration.
                      </div>
                    </div>
                  )}

                  {/* Otter Analytics Section */}
                  {enabled && apiKey && apiSecret && storeId && (
                    <OtterAnalyticsSection tenantId={form?.tenant_id} />
                  )}
                </section>
              </details>
            );
          })()}

          <details open>
            <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Shipping rates</summary>

          <div style={{ display: 'grid', gap: 18, marginTop: 14 }}>
            <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
              <h3 style={{ marginBottom: 12 }}>Standard shipping</h3>
              <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={Boolean(standard.enabled)}
                  onChange={(e) => updateShipping('standard', { enabled: e.target.checked })}
                />
                Enabled
              </label>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Label</label>
                  <input
                    value={standard.label || ''}
                    onChange={(e) => updateShipping('standard', { label: e.target.value })}
                    placeholder="Standard Shipping"
                  />
                </div>
                <div className="form-group">
                  <label>Price (USD)</label>
                  <input
                    value={centsToDollars(standard.amount_cents)}
                    onChange={(e) => updateShipping('standard', { amount_cents: dollarsToCents(e.target.value) ?? standard.amount_cents })}
                    placeholder="4.99"
                  />
                </div>
              </div>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Min days</label>
                  <input
                    type="number"
                    value={standard.min_days ?? 3}
                    onChange={(e) => updateShipping('standard', { min_days: Number(e.target.value) })}
                  />
                </div>
                <div className="form-group">
                  <label>Max days</label>
                  <input
                    type="number"
                    value={standard.max_days ?? 5}
                    onChange={(e) => updateShipping('standard', { max_days: Number(e.target.value) })}
                  />
                </div>
              </div>
            </section>

            <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
              <h3 style={{ marginBottom: 12 }}>Express shipping</h3>
              <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={Boolean(express.enabled)}
                  onChange={(e) => updateShipping('express', { enabled: e.target.checked })}
                />
                Enabled
              </label>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Label</label>
                  <input
                    value={express.label || ''}
                    onChange={(e) => updateShipping('express', { label: e.target.value })}
                    placeholder="Express Shipping"
                  />
                </div>
                <div className="form-group">
                  <label>Price (USD)</label>
                  <input
                    value={centsToDollars(express.amount_cents)}
                    onChange={(e) => updateShipping('express', { amount_cents: dollarsToCents(e.target.value) ?? express.amount_cents })}
                    placeholder="9.99"
                    disabled={!express.enabled}
                  />
                </div>
              </div>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Min days</label>
                  <input
                    type="number"
                    value={express.min_days ?? 1}
                    onChange={(e) => updateShipping('express', { min_days: Number(e.target.value) })}
                    disabled={!express.enabled}
                  />
                </div>
                <div className="form-group">
                  <label>Max days</label>
                  <input
                    type="number"
                    value={express.max_days ?? 2}
                    onChange={(e) => updateShipping('express', { max_days: Number(e.target.value) })}
                    disabled={!express.enabled}
                  />
                </div>
              </div>
            </section>

            <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
              <h3 style={{ marginBottom: 12 }}>Free shipping</h3>
              <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={Boolean(free.enabled)}
                  onChange={(e) => updateShipping('free', { enabled: e.target.checked })}
                />
                Enabled
              </label>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Label</label>
                  <input
                    value={free.label || ''}
                    onChange={(e) => updateShipping('free', { label: e.target.value })}
                    placeholder="Free Shipping"
                    disabled={!free.enabled}
                  />
                </div>
                <div className="form-group">
                  <label>Free over (USD)</label>
                  <input
                    value={centsToDollars(free.threshold_cents)}
                    onChange={(e) => updateShipping('free', { threshold_cents: dollarsToCents(e.target.value) ?? free.threshold_cents })}
                    placeholder="20.00"
                    disabled={!free.enabled}
                  />
                </div>
              </div>

              <div className="form-row" style={{ gap: 14 }}>
                <div className="form-group">
                  <label>Min days</label>
                  <input
                    type="number"
                    value={free.min_days ?? 5}
                    onChange={(e) => updateShipping('free', { min_days: Number(e.target.value) })}
                    disabled={!free.enabled}
                  />
                </div>
                <div className="form-group">
                  <label>Max days</label>
                  <input
                    type="number"
                    value={free.max_days ?? 8}
                    onChange={(e) => updateShipping('free', { max_days: Number(e.target.value) })}
                    disabled={!free.enabled}
                  />
                </div>
              </div>
            </section>
          </div>
          </details>

          {(() => {
            const mode = String(form?.storeMode ?? '').trim().toUpperCase();
            if (mode === 'POS') return null;
            return (
              <details open style={{ marginTop: 22 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Taxes</summary>
                <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)', marginTop: 14 }}>
                  <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(form?.tax?.stripe_automatic_tax_enabled)}
                      onChange={(e) => updateTax({ stripe_automatic_tax_enabled: e.target.checked })}
                    />
                    Enable automatic tax
                  </label>
                  <p style={{ margin: 0, color: '#475569', fontSize: '0.95rem' }}>
                    Requires tax configuration in your payment dashboard (registrations, tax settings).
                  </p>
                </section>
              </details>
            );
          })()}

          <details open style={{ marginTop: 22 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>Optional Sections</summary>
            <div style={{ display: 'grid', gap: 18, marginTop: 14 }}>
              <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
                <h3 style={{ marginBottom: 12 }}>Product features</h3>
                <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(form?.optional_sections?.crochet_features?.enabled)}
                    onChange={(e) => updateOptionalCrochetFeatures({ enabled: e.target.checked })}
                  />
                  Enabled
                </label>

                <div className="form-row" style={{ gap: 14 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Title</label>
                    <input
                      value={String(form?.optional_sections?.crochet_features?.title || 'Product features')}
                      onChange={(e) => updateOptionalCrochetFeatures({ title: e.target.value })}
                      placeholder="Product features"
                      disabled={!form?.optional_sections?.crochet_features?.enabled}
                    />
                  </div>
                </div>

                <div className="form-row" style={{ gap: 14 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>HTML content</label>
                    <textarea
                      rows={7}
                      value={String(form?.optional_sections?.crochet_features?.html || '')}
                      onChange={(e) => updateOptionalCrochetFeatures({ html: e.target.value })}
                      placeholder="Add HTML-enabled feature bullets"
                      disabled={!form?.optional_sections?.crochet_features?.enabled}
                      style={{ width: '100%' }}
                    />
                    <div className="field-hint">Rendered on the demo app home page above Events.</div>
                  </div>
                </div>
              </section>

              <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
                <h3 style={{ marginBottom: 12 }}>Events</h3>
                <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(form?.optional_sections?.events?.enabled)}
                    onChange={(e) => updateOptionalEvents({ enabled: e.target.checked })}
                  />
                  Enabled
                </label>

                <div className="form-row" style={{ gap: 14 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Title</label>
                    <input
                      value={String(form?.optional_sections?.events?.title || 'Events')}
                      onChange={(e) => updateOptionalEvents({ title: e.target.value })}
                      placeholder="Events"
                      disabled={!form?.optional_sections?.events?.enabled}
                    />
                  </div>
                </div>

                <div className="form-row" style={{ gap: 14 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>HTML content</label>
                    <textarea
                      rows={6}
                      value={String(form?.optional_sections?.events?.html || '')}
                      onChange={(e) => updateOptionalEvents({ html: e.target.value })}
                      placeholder="Paste HTML-enabled events feed content"
                      disabled={!form?.optional_sections?.events?.enabled}
                      style={{ width: '100%' }}
                    />
                    <div className="field-hint">Rendered on the demo app home page below the hero.</div>
                  </div>
                </div>
              </section>

              <section style={{ background: '#fff', borderRadius: 16, padding: 18, border: '1px solid rgba(148, 163, 184, 0.35)' }}>
                <h3 style={{ marginBottom: 12 }}>Reviews</h3>
                <label className="toggle-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(form?.optional_sections?.reviews?.enabled)}
                    onChange={(e) => updateOptionalReviews({ enabled: e.target.checked })}
                  />
                  Enabled
                </label>

                <div className="form-row" style={{ gap: 14 }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Title</label>
                    <input
                      value={String(form?.optional_sections?.reviews?.title || 'Reviews')}
                      onChange={(e) => updateOptionalReviews({ title: e.target.value })}
                      placeholder="Reviews"
                      disabled={!form?.optional_sections?.reviews?.enabled}
                    />
                  </div>
                  <div className="form-group" style={{ minWidth: 260 }}>
                    <label>Approval mode</label>
                    <select
                      value={Boolean(form?.optional_sections?.reviews?.auto_approve) ? 'auto' : 'admin'}
                      onChange={(e) => updateOptionalReviews({ auto_approve: e.target.value === 'auto' })}
                      disabled={!form?.optional_sections?.reviews?.enabled}
                    >
                      <option value="admin">Admin approval required</option>
                      <option value="auto">Auto-approve after verification</option>
                    </select>
                    <div className="field-hint">Guests must confirm via email (double opt-in). Logged-in users skip email verification.</div>
                  </div>
                </div>
              </section>
            </div>
          </details>

          <div className="general-actions-bar">
            <button className="btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn-secondary" onClick={fetchSettings} disabled={saving}>
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
